import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import clsx, { clsx as clsx$1 } from 'clsx';
import { forwardRef, createContext, useContext, useCallback, useMemo, useState, useRef, useEffect, useImperativeHandle, useId, startTransition } from 'react';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon, AlertIcon, InfoIcon, CheckmarkIcon, PersonalLoginIcon, RadioIcon, SearchIcon, ChevronDownIcon } from '@amsterdam/design-system-react-icons';

var _excluded$1u = ["children", "className"];
function ownKeys$1A(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1A(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1A(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1A(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ActionGroup = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$1u);
  return jsx("div", _objectSpread$1A(_objectSpread$1A({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-action-group', className),
    role: "group",
    children: children
  }));
});
ActionGroup.displayName = 'ActionGroup';

/**
 * @license EUPL-1.2+
 * Copyright Gemeente Amsterdam
 */
var addGridClass = function addGridClass(prefix, value) {
  if (value === 'all' || typeof value === 'number') {
    return ["".concat(prefix).concat(value)];
  } else if (value) {
    var narrow = value.narrow,
      medium = value.medium,
      wide = value.wide;
    return ["".concat(prefix).concat(narrow), "".concat(prefix).concat(medium, "-medium"), "".concat(prefix).concat(wide, "-wide")];
  }
  return [];
};
var gridCellClasses = function gridCellClasses(colSpan, colStart) {
  return [].concat(_toConsumableArray(addGridClass('ams-grid__cell--span-', colSpan)), _toConsumableArray(addGridClass('ams-grid__cell--start-', colStart)));
};

var breakoutCellClasses = function breakoutCellClasses(colSpan, colStart, rowSpan, rowStart) {
  return [].concat(_toConsumableArray(addGridClass('ams-breakout__cell--col-span-', colSpan)), _toConsumableArray(addGridClass('ams-breakout__cell--col-start-', colStart)), _toConsumableArray(addGridClass('ams-breakout__cell--row-span-', rowSpan)), _toConsumableArray(addGridClass('ams-breakout__cell--row-start-', rowStart)));
};

var _excluded$1t = ["as", "children", "className", "colSpan", "colStart", "has", "rowSpan", "rowStart"];
function ownKeys$1z(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1z(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1z(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1z(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var BreakoutCell = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$as = _ref.as,
    Tag = _ref$as === void 0 ? 'div' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    colSpan = _ref.colSpan,
    colStart = _ref.colStart,
    has = _ref.has,
    rowSpan = _ref.rowSpan,
    rowStart = _ref.rowStart,
    restProps = _objectWithoutProperties(_ref, _excluded$1t);
  return jsx(Tag, _objectSpread$1z(_objectSpread$1z({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-breakout__cell', breakoutCellClasses(colSpan, colStart, rowSpan, rowStart), has && "ams-breakout__cell--has-".concat(has), className),
    children: children
  }));
});
BreakoutCell.displayName = 'Breakout.Cell';

var paddingClasses = function paddingClasses(componentName, paddingBottom, paddingTop, paddingVertical) {
  var classes = [];
  if (paddingVertical) {
    return ["ams-".concat(componentName, "--padding-vertical--").concat(paddingVertical)];
  }
  if (paddingBottom) {
    classes.push("ams-".concat(componentName, "--padding-bottom--").concat(paddingBottom));
  }
  if (paddingTop) {
    classes.push("ams-".concat(componentName, "--padding-top--").concat(paddingTop));
  }
  return classes;
};

var _excluded$1s = ["children", "className", "gapVertical", "paddingBottom", "paddingTop", "paddingVertical"];
function ownKeys$1y(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1y(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1y(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1y(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var BreakoutRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    gapVertical = _ref.gapVertical,
    paddingBottom = _ref.paddingBottom,
    paddingTop = _ref.paddingTop,
    paddingVertical = _ref.paddingVertical,
    restProps = _objectWithoutProperties(_ref, _excluded$1s);
  return jsx("div", _objectSpread$1y(_objectSpread$1y({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-breakout', gapVertical && "ams-breakout--gap-vertical--".concat(gapVertical), paddingClasses('breakout', paddingBottom, paddingTop, paddingVertical), className),
    children: children
  }));
});
BreakoutRoot.displayName = 'Breakout';
var Breakout = Object.assign(BreakoutRoot, {
  Cell: BreakoutCell
});

var _excluded$1r = ["className", "hint", "optional"];
function ownKeys$1x(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1x(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1x(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1x(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getHintText = function getHintText(_ref) {
  var hint = _ref.hint,
    optional = _ref.optional;
  if (hint) {
    return hint;
  } else if (optional) {
    return 'niet verplicht';
  }
  return null;
};
var Hint = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    hint = _ref2.hint,
    optional = _ref2.optional,
    restProps = _objectWithoutProperties(_ref2, _excluded$1r);
  var hintText = getHintText({
    hint: hint,
    optional: optional
  });
  return hintText && jsxs("span", _objectSpread$1x(_objectSpread$1x({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-hint', className),
    children: ["(", hintText, ")"]
  }));
});
Hint.displayName = 'Hint';

/**
 * @license EUPL-1.2+
 * Copyright Gemeente Amsterdam
 */
var defaultValues$2 = {
  currentSlideId: 0,
  isAtStart: true,
  isAtEnd: false,
  goToNextSlide: function goToNextSlide() {},
  goToPreviousSlide: function goToPreviousSlide() {},
  goToSlideId: function goToSlideId() {}
};
var ImageSliderContext = /*#__PURE__*/createContext(defaultValues$2);

var _excluded$1q = ["className", "size", "square", "svg"];
function ownKeys$1w(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1w(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1w(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1w(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Icon = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'level-3' : _ref$size,
    square = _ref.square,
    svg = _ref.svg,
    restProps = _objectWithoutProperties(_ref, _excluded$1q);
  return jsx("span", _objectSpread$1w(_objectSpread$1w({
    ref: ref,
    className: clsx('ams-icon', size === 'level-3' && 'ams-icon--size-3', size === 'level-4' && 'ams-icon--size-4', size === 'level-5' && 'ams-icon--size-5', size === 'level-6' && 'ams-icon--size-6', square && 'ams-icon--square', className)
  }, restProps), {}, {
    children: svg()
  }));
});
Icon.displayName = 'Icon';

var _excluded$1p = ["className", "label", "contrastColor", "inverseColor", "size", "svg"];
function ownKeys$1v(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1v(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1v(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1v(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var IconButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    label = _ref.label,
    contrastColor = _ref.contrastColor,
    inverseColor = _ref.inverseColor,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'level-5' : _ref$size,
    _ref$svg = _ref.svg,
    svg = _ref$svg === void 0 ? CloseIcon : _ref$svg,
    restProps = _objectWithoutProperties(_ref, _excluded$1p);
  return jsxs("button", _objectSpread$1v(_objectSpread$1v({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-icon-button', contrastColor && 'ams-icon-button--contrast-color', inverseColor && 'ams-icon-button--inverse-color', className),
    children: [jsx("span", {
      className: "ams-visually-hidden",
      children: label
    }), jsx(Icon, {
      svg: svg,
      size: size,
      square: true
    })]
  }));
});
IconButton.displayName = 'IconButton';

var _excluded$1o = ["className", "nextLabel", "previousLabel"];
function ownKeys$1u(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1u(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1u(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1u(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ImageSliderControls = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    nextLabel = _ref.nextLabel,
    previousLabel = _ref.previousLabel,
    restProps = _objectWithoutProperties(_ref, _excluded$1o);
  var _useContext = useContext(ImageSliderContext),
    isAtStart = _useContext.isAtStart,
    isAtEnd = _useContext.isAtEnd,
    goToNextSlide = _useContext.goToNextSlide,
    goToPreviousSlide = _useContext.goToPreviousSlide;
  var handleClickPrevious = useCallback(function () {
    return goToPreviousSlide();
  }, [goToPreviousSlide]);
  var handleClickNext = useCallback(function () {
    return goToNextSlide();
  }, [goToNextSlide]);
  return jsxs("div", _objectSpread$1u(_objectSpread$1u({}, restProps), {}, {
    className: clsx('ams-image-slider__controls', className),
    ref: ref,
    children: [jsx(IconButton, {
      className: "ams-image-slider__control ams-image-slider__control--previous",
      disabled: isAtStart,
      inverseColor: true,
      label: previousLabel,
      onClick: handleClickPrevious,
      svg: ChevronLeftIcon
    }), jsx(IconButton, {
      className: "ams-image-slider__control ams-image-slider__control--next",
      disabled: isAtEnd,
      inverseColor: true,
      label: nextLabel,
      onClick: handleClickNext,
      svg: ChevronRightIcon
    })]
  }));
});
ImageSliderControls.displayName = 'ImageSliderControls';

var _excluded$1n = ["children", "slideId", "className"];
function ownKeys$1t(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1t(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1t(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1t(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ImageSliderItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    slideId = _ref.slideId,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$1n);
  var _useContext = useContext(ImageSliderContext),
    currentSlideId = _useContext.currentSlideId;
  var isInView = useMemo(function () {
    return currentSlideId === slideId;
  }, [currentSlideId, slideId]);
  var itemClassName = useMemo(function () {
    return clsx('ams-image-slider__item', isInView && 'ams-image-slider__item--in-view', className);
  }, [isInView, className]);
  return jsx("div", _objectSpread$1t(_objectSpread$1t(_objectSpread$1t({}, restProps), {}, {
    className: itemClassName,
    ref: ref
  }, !isInView && {
    inert: ''
  }), {}, {
    children: children
  }));
});
ImageSliderItem.displayName = 'ImageSlider.Item';

var _excluded$1m = ["children", "className"];
function ownKeys$1s(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1s(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1s(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1s(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ImageSliderScroller = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$1m);
  return jsx("div", _objectSpread$1s(_objectSpread$1s({}, restProps), {}, {
    className: clsx('ams-image-slider__scroller', className),
    ref: ref,
    children: children
  }));
});
ImageSliderScroller.displayName = 'ImageSlider.Scroller';

var _excluded$1l = ["className", "imageLabel", "thumbnails"];
function ownKeys$1r(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1r(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1r(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1r(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ImageSliderThumbnails = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    imageLabel = _ref.imageLabel,
    thumbnails = _ref.thumbnails,
    restProps = _objectWithoutProperties(_ref, _excluded$1l);
  var _useContext = useContext(ImageSliderContext),
    currentSlideId = _useContext.currentSlideId,
    goToNextSlide = _useContext.goToNextSlide,
    goToPreviousSlide = _useContext.goToPreviousSlide,
    goToSlideId = _useContext.goToSlideId;
  var handleKeyDown = useCallback(function (event) {
    var element = event.currentTarget.children[currentSlideId];
    if (event.key === 'ArrowRight') {
      var nextElement = element === null || element === void 0 ? void 0 : element.nextElementSibling;
      if (nextElement) {
        nextElement.focus();
        goToNextSlide();
      }
    }
    if (event.key === 'ArrowLeft') {
      var previousElement = element === null || element === void 0 ? void 0 : element.previousElementSibling;
      if (previousElement) {
        previousElement.focus();
        goToPreviousSlide();
      }
    }
  }, [currentSlideId, goToNextSlide, goToPreviousSlide]);
  var renderThumbnails = useMemo(function () {
    return thumbnails.map(function (_ref2, index) {
      var alt = _ref2.alt,
        aspectRatio = _ref2.aspectRatio,
        src = _ref2.src;
      return jsx("button", {
        "aria-label": "".concat(imageLabel, " ").concat(index + 1, ": ").concat(alt),
        "aria-posinset": index + 1,
        "aria-selected": currentSlideId === index ? 'true' : 'false',
        "aria-setsize": thumbnails.length,
        className: clsx('ams-image-slider__thumbnail', currentSlideId === index && 'ams-image-slider__thumbnail--in-view', aspectRatio && "ams-aspect-ratio--".concat(aspectRatio)),
        onClick: function onClick() {
          return goToSlideId(index);
        },
        role: "tab",
        style: {
          backgroundImage: "url(".concat(src, ")")
        },
        tabIndex: currentSlideId === index ? 0 : -1
      }, index);
    });
  }, [currentSlideId, goToSlideId, imageLabel, thumbnails]);
  return jsx("nav", _objectSpread$1r(_objectSpread$1r({}, restProps), {}, {
    className: clsx('ams-image-slider__thumbnails', className),
    onKeyDown: handleKeyDown,
    ref: ref,
    role: "tablist",
    children: renderThumbnails
  }));
});
ImageSliderThumbnails.displayName = 'ImageSlider.Thumbnails';

var _excluded$1k = ["className", "cover"];
function ownKeys$1q(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1q(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1q(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1q(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Image = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$cover = _ref.cover,
    cover = _ref$cover === void 0 ? false : _ref$cover,
    restProps = _objectWithoutProperties(_ref, _excluded$1k);
  return jsx("img", _objectSpread$1q(_objectSpread$1q({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-image', cover && 'ams-image--cover', className)
  }));
});
Image.displayName = 'Image';

var _excluded$1j = ["className", "controls", "imageLabel", "images", "nextLabel", "previousLabel"];
function ownKeys$1p(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1p(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1p(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1p(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ImageSliderRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    controls = _ref.controls,
    _ref$imageLabel = _ref.imageLabel,
    imageLabel = _ref$imageLabel === void 0 ? 'Afbeelding' : _ref$imageLabel,
    images = _ref.images,
    _ref$nextLabel = _ref.nextLabel,
    nextLabel = _ref$nextLabel === void 0 ? 'Volgende' : _ref$nextLabel,
    _ref$previousLabel = _ref.previousLabel,
    previousLabel = _ref$previousLabel === void 0 ? 'Vorige' : _ref$previousLabel,
    restProps = _objectWithoutProperties(_ref, _excluded$1j);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentSlideId = _useState2[0],
    setCurrentSlideId = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isAtStart = _useState4[0],
    setIsAtStart = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isAtEnd = _useState6[0],
    setIsAtEnd = _useState6[1];
  var targetRef = useRef(null);
  var observerRef = useRef(null);
  var inView = useCallback(function (observations) {
    var _targetRef$current;
    var images = Array.from(((_targetRef$current = targetRef.current) === null || _targetRef$current === void 0 ? void 0 : _targetRef$current.children) || []);
    observations.forEach(function (observation) {
      if (observation.isIntersecting) {
        setCurrentSlideId(images.indexOf(observation.target));
      }
    });
  }, []);
  var observerOptions = useMemo(function () {
    return {
      root: targetRef.current,
      threshold: 0.6
    };
  }, []);
  var updateControls = useCallback(function () {
    var sliderScrollerElement = targetRef.current;
    if (!sliderScrollerElement) return;
    var lastElement = sliderScrollerElement.lastElementChild,
      firstElement = sliderScrollerElement.firstElementChild;
    setIsAtStart(firstElement === (sliderScrollerElement === null || sliderScrollerElement === void 0 ? void 0 : sliderScrollerElement.children[currentSlideId]));
    setIsAtEnd(lastElement === (sliderScrollerElement === null || sliderScrollerElement === void 0 ? void 0 : sliderScrollerElement.children[currentSlideId]));
  }, [currentSlideId]);
  useEffect(function () {
    if (targetRef.current) {
      observerRef.current = new IntersectionObserver(inView, observerOptions);
      var observer = observerRef.current;
      var slides = Array.from(targetRef.current.children);
      slides.forEach(function (slide) {
        return observer.observe(slide);
      });
      targetRef.current.addEventListener('scrollend', synchronise);
      updateControls();
      return function () {
        var _targetRef$current2;
        slides.forEach(function (slide) {
          return observer.unobserve(slide);
        });
        (_targetRef$current2 = targetRef.current) === null || _targetRef$current2 === void 0 || _targetRef$current2.removeEventListener('scrollend', synchronise);
      };
    }
    return undefined;
  }, [inView, observerOptions, updateControls]);
  var synchronise = useCallback(function () {
    return updateControls();
  }, [updateControls]);
  var goToSlide = useCallback(function (element) {
    var sliderScrollerElement = targetRef.current;
    if (!sliderScrollerElement || !element) return;
    sliderScrollerElement.scrollTo({
      left: sliderScrollerElement.scrollLeft + element.offsetLeft - sliderScrollerElement.scrollLeft
    });
  }, []);
  var goToSlideId = useCallback(function (id) {
    var _targetRef$current3;
    var element = (_targetRef$current3 = targetRef.current) === null || _targetRef$current3 === void 0 ? void 0 : _targetRef$current3.children[id];
    if (element) goToSlide(element);
  }, [goToSlide]);
  var goToNextSlide = useCallback(function () {
    var _targetRef$current4;
    var element = (_targetRef$current4 = targetRef.current) === null || _targetRef$current4 === void 0 ? void 0 : _targetRef$current4.children[currentSlideId];
    var nextElement = element === null || element === void 0 ? void 0 : element.nextElementSibling;
    if (nextElement) goToSlide(nextElement);
  }, [currentSlideId, goToSlide]);
  var goToPreviousSlide = useCallback(function () {
    var _targetRef$current5;
    var element = (_targetRef$current5 = targetRef.current) === null || _targetRef$current5 === void 0 ? void 0 : _targetRef$current5.children[currentSlideId];
    var previousElement = element === null || element === void 0 ? void 0 : element.previousElementSibling;
    if (previousElement) goToSlide(previousElement);
  }, [currentSlideId, goToSlide]);
  useEffect(function () {
    var handleResize = function handleResize() {
      var _targetRef$current6;
      var sliderScrollerElement = targetRef.current;
      var currentSlideElement = (_targetRef$current6 = targetRef.current) === null || _targetRef$current6 === void 0 ? void 0 : _targetRef$current6.children[currentSlideId];
      if (!sliderScrollerElement || !currentSlideElement) return;
      var expectedScrollLeft = currentSlideElement.offsetLeft;
      if (Math.abs(sliderScrollerElement.scrollLeft - expectedScrollLeft) < 1) return;
      goToSlide(currentSlideElement);
    };
    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, [currentSlideId, goToSlide]);
  return jsx(ImageSliderContext.Provider, {
    value: {
      isAtStart: isAtStart,
      isAtEnd: isAtEnd,
      currentSlideId: currentSlideId,
      goToNextSlide: goToNextSlide,
      goToPreviousSlide: goToPreviousSlide,
      goToSlideId: goToSlideId
    },
    children: jsxs("div", _objectSpread$1p(_objectSpread$1p({}, restProps), {}, {
      "aria-roledescription": "carousel",
      className: clsx('ams-image-slider', controls && 'ams-image-slider--controls', className),
      tabIndex: -1,
      ref: ref,
      children: [controls && jsx(ImageSliderControls, {
        nextLabel: nextLabel,
        previousLabel: previousLabel
      }), jsx(ImageSliderScroller, {
        "aria-live": "polite",
        ref: targetRef,
        role: "group",
        tabIndex: 0,
        children: images.map(function (_ref2, index) {
          var alt = _ref2.alt,
            aspectRatio = _ref2.aspectRatio,
            sizes = _ref2.sizes,
            src = _ref2.src,
            srcSet = _ref2.srcSet;
          return jsx(ImageSliderItem, {
            slideId: index,
            children: jsx(Image, {
              alt: alt,
              className: "ams-aspect-ratio--".concat(aspectRatio),
              sizes: sizes,
              src: src,
              srcSet: srcSet
            })
          }, index);
        })
      }), jsx(ImageSliderThumbnails, {
        imageLabel: imageLabel,
        thumbnails: images
      })]
    }))
  });
});
ImageSliderRoot.displayName = 'ImageSlider';
var ImageSlider = Object.assign(ImageSliderRoot, {
  Item: ImageSliderItem
});

var _excluded$1i = ["className", "dir", "invalid"];
function ownKeys$1o(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1o(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1o(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1o(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PasswordInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    dir = _ref.dir,
    invalid = _ref.invalid,
    restProps = _objectWithoutProperties(_ref, _excluded$1i);
  return jsx("input", _objectSpread$1o(_objectSpread$1o({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    autoCorrect: "off",
    className: clsx('ams-password-input', className),
    dir: dir !== null && dir !== void 0 ? dir : 'auto',
    ref: ref,
    spellCheck: "false",
    type: "password"
  }));
});
PasswordInput.displayName = 'PasswordInput';

function getHeadingTag(level) {
  switch (level) {
    case 2:
      return 'h2';
    case 3:
      return 'h3';
    case 4:
      return 'h4';
    default:
      return 'h1';
  }
}

var _excluded$1h = ["children", "className", "inverseColor", "level", "size"];
function ownKeys$1n(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1n(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1n(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1n(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Heading = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    _ref$level = _ref.level,
    level = _ref$level === void 0 ? 1 : _ref$level,
    size = _ref.size,
    restProps = _objectWithoutProperties(_ref, _excluded$1h);
  var Tag = getHeadingTag(level);
  var sizeOrLevel = size !== null && size !== void 0 ? size : "level-".concat(level);
  return jsx(Tag, _objectSpread$1n(_objectSpread$1n({
    ref: ref,
    className: clsx('ams-heading', "ams-heading--".concat(sizeOrLevel), inverseColor && 'ams-heading--inverse-color', className)
  }, restProps), {}, {
    children: children
  }));
});
Heading.displayName = 'Heading';

var _excluded$1g = ["children", "className", "closeable", "closeButtonLabel", "heading", "headingLevel", "onClose", "severity"];
function ownKeys$1m(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1m(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1m(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1m(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var iconSvgBySeverity = {
  error: AlertIcon,
  info: InfoIcon,
  success: CheckmarkIcon,
  warning: AlertIcon
};
var Alert = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    closeable = _ref.closeable,
    _ref$closeButtonLabel = _ref.closeButtonLabel,
    closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'Sluiten' : _ref$closeButtonLabel,
    heading = _ref.heading,
    _ref$headingLevel = _ref.headingLevel,
    headingLevel = _ref$headingLevel === void 0 ? 2 : _ref$headingLevel,
    onClose = _ref.onClose,
    _ref$severity = _ref.severity,
    severity = _ref$severity === void 0 ? 'warning' : _ref$severity,
    restProps = _objectWithoutProperties(_ref, _excluded$1g);
  var alertSize = heading ? 'level-4' : 'level-5';
  var Tag = heading ? 'section' : 'div';
  return jsxs(Tag, _objectSpread$1m(_objectSpread$1m({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-alert', severity && "ams-alert--".concat(severity), className),
    children: [jsx("div", {
      className: "ams-alert__icon",
      children: jsx(Icon, {
        size: alertSize,
        svg: iconSvgBySeverity[severity]
      })
    }), jsxs("div", {
      className: "ams-alert__content",
      children: [heading && jsx(Heading, {
        level: headingLevel,
        size: "level-4",
        children: heading
      }), children]
    }), closeable && jsx(IconButton, {
      label: closeButtonLabel,
      size: alertSize,
      onClick: onClose
    })]
  }));
});
Alert.displayName = 'Alert';

var _excluded$1f = ["children", "className", "icon", "contrastColor", "inverseColor", "size"];
function ownKeys$1l(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1l(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1l(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1l(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var iconSizeMap = {
  small: 'level-6',
  medium: 'level-5',
  large: 'level-4'
};
/** One link with a Link List. */
var LinkListLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    icon = _ref.icon,
    contrastColor = _ref.contrastColor,
    inverseColor = _ref.inverseColor,
    size = _ref.size,
    restProps = _objectWithoutProperties(_ref, _excluded$1f);
  return jsx("li", {
    children: jsxs("a", _objectSpread$1l(_objectSpread$1l({
      className: clsx('ams-link-list__link', contrastColor && 'ams-link-list__link--contrast-color', inverseColor && 'ams-link-list__link--inverse-color', size && "ams-link-list__link--".concat(size), className),
      ref: ref
    }, restProps), {}, {
      children: [jsx(Icon, {
        svg: icon !== null && icon !== void 0 ? icon : ChevronRightIcon,
        size: iconSizeMap[size !== null && size !== void 0 ? size : 'medium']
      }), children]
    }))
  });
});
LinkListLink.displayName = 'LinkList.Link';

var _excluded$1e = ["children", "className"];
function ownKeys$1k(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1k(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1k(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1k(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** A collection of related links. */
var LinkListRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$1e);
  return jsx("ul", _objectSpread$1k(_objectSpread$1k({
    ref: ref,
    className: clsx('ams-link-list', className)
  }, restProps), {}, {
    children: children
  }));
});
LinkListRoot.displayName = 'LinkList';
var LinkList = Object.assign(LinkListRoot, {
  Link: LinkListLink
});

var _excluded$1d = ["className", "errors", "focusOnRender", "hasFocusedOnce", "heading", "headingLevel", "setHasFocusedOnce"];
function ownKeys$1j(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1j(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1j(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1j(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FormErrorListWithErrors = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    errors = _ref.errors,
    _ref$focusOnRender = _ref.focusOnRender,
    focusOnRender = _ref$focusOnRender === void 0 ? true : _ref$focusOnRender,
    hasFocusedOnce = _ref.hasFocusedOnce,
    _ref$heading = _ref.heading,
    heading = _ref$heading === void 0 ? 'Verbeter de fouten voor u verder gaat' : _ref$heading,
    _ref$headingLevel = _ref.headingLevel,
    headingLevel = _ref$headingLevel === void 0 ? 2 : _ref$headingLevel,
    setHasFocusedOnce = _ref.setHasFocusedOnce,
    restProps = _objectWithoutProperties(_ref, _excluded$1d);
  var innerRef = useRef(null);
  // use a passed ref if it's there, otherwise use innerRef
  useImperativeHandle(ref, function () {
    return innerRef.current;
  });
  useEffect(function () {
    if (innerRef.current && focusOnRender && !hasFocusedOnce) {
      innerRef.current.focus();
      setHasFocusedOnce(true);
    }
  }, [innerRef]);
  return jsx(Alert, _objectSpread$1j(_objectSpread$1j({}, restProps), {}, {
    className: clsx('ams-form-error-list', className),
    heading: heading,
    headingLevel: headingLevel,
    ref: innerRef,
    severity: "error",
    tabIndex: -1,
    children: jsx(LinkList, {
      children: errors.map(function (_ref2) {
        var id = _ref2.id,
          label = _ref2.label;
        return jsx(LinkList.Link, {
          href: id,
          children: label
        }, "".concat(id, "-").concat(label));
      })
    })
  }));
});
FormErrorListWithErrors.displayName = 'FormErrorListWithErrors';

var useAddErrorCountToDocumentTitle = function useAddErrorCountToDocumentTitle(/** The list of error messages used to calculate the error count. */
errors) {
  var label = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    plural: 'invoerfouten',
    singular: 'invoerfout'
  };
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    documentTitle = _useState2[0],
    setDocumentTitle = _useState2[1];
  useEffect(function () {
    setDocumentTitle(document.title);
  }, []);
  if (!documentTitle) return null;
  if (errors.length === 1) {
    document.title = "(".concat(errors.length, " ").concat(label.singular, ") ").concat(documentTitle);
  } else if (errors.length > 1) {
    document.title = "(".concat(errors.length, " ").concat(label.plural, ") ").concat(documentTitle);
  } else {
    document.title = documentTitle;
  }
  return null;
};

var _excluded$1c = ["errors", "errorCountLabel"];
function ownKeys$1i(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1i(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1i(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1i(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FormErrorList = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var errors = _ref.errors,
    errorCountLabel = _ref.errorCountLabel,
    restProps = _objectWithoutProperties(_ref, _excluded$1c);
  // A Form Error List without errors only resets the document title.
  // With errors, it renders the FormErrorListWithErrors component.
  useAddErrorCountToDocumentTitle(errors, errorCountLabel);
  // Focus should only be set on first render of FormErrorListWithErrors.
  // Subsequent renders should not set focus.
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasFocusedOnce = _useState2[0],
    setHasFocusedOnce = _useState2[1];
  if (errors.length === 0) return undefined;
  return jsx(FormErrorListWithErrors, _objectSpread$1i(_objectSpread$1i({}, restProps), {}, {
    errors: errors,
    hasFocusedOnce: hasFocusedOnce,
    ref: ref,
    setHasFocusedOnce: setHasFocusedOnce
  }));
});
FormErrorList.displayName = 'FormErrorList';

var _excluded$1b = ["children", "className", "label"];
function ownKeys$1h(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1h(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1h(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1h(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableOfContentsLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    label = _ref.label,
    restProps = _objectWithoutProperties(_ref, _excluded$1b);
  return jsxs("li", {
    className: "ams-table-of-contents__item",
    children: [jsx("a", _objectSpread$1h(_objectSpread$1h({}, restProps), {}, {
      className: clsx$1('ams-table-of-contents__link', className),
      ref: ref,
      children: label
    })), children]
  });
});
TableOfContentsLink.displayName = 'TableOfContents.Link';

var _excluded$1a = ["children", "className"];
function ownKeys$1g(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1g(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1g(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1g(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableOfContentsList = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$1a);
  return jsx("ul", _objectSpread$1g(_objectSpread$1g({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table-of-contents__list', className),
    children: children
  }));
});
TableOfContentsList.displayName = 'TableOfContents.List';

var _excluded$19 = ["children", "className", "heading", "headingLevel"];
function ownKeys$1f(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1f(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1f(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1f(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableOfContentsRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    heading = _ref.heading,
    _ref$headingLevel = _ref.headingLevel,
    headingLevel = _ref$headingLevel === void 0 ? 2 : _ref$headingLevel,
    restProps = _objectWithoutProperties(_ref, _excluded$19);
  return jsxs("nav", _objectSpread$1f(_objectSpread$1f({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table-of-contents', className),
    children: [heading && jsx(Heading, {
      className: "ams-table-of-contents__heading",
      level: headingLevel,
      size: "level-4",
      children: heading
    }), children]
  }));
});
TableOfContentsRoot.displayName = 'TableOfContents';
var TableOfContents = Object.assign(TableOfContentsRoot, {
  Link: TableOfContentsLink,
  List: TableOfContentsList
});

var _excluded$18 = ["children", "className", "prefix"];
function ownKeys$1e(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1e(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1e(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1e(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var ErrorMessage = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$prefix = _ref.prefix,
    prefix = _ref$prefix === void 0 ? 'Invoerfout' : _ref$prefix,
    restProps = _objectWithoutProperties(_ref, _excluded$18);
  return jsxs("p", _objectSpread$1e(_objectSpread$1e({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-error-message', className),
    children: [jsxs("span", {
      className: "ams-visually-hidden",
      children: [prefix, ': ']
    }), children]
  }));
});
ErrorMessage.displayName = 'ErrorMessage';

var _excluded$17 = ["className"];
function ownKeys$1d(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1d(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1d(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1d(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FileInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$17);
  return jsx("input", _objectSpread$1d(_objectSpread$1d({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-file-input', className),
    type: "file"
  }));
});
FileInput.displayName = 'FileInput';

var _excluded$16 = ["children", "className", "invalid"];
function ownKeys$1c(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1c(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1c(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1c(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Field = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    invalid = _ref.invalid,
    restProps = _objectWithoutProperties(_ref, _excluded$16);
  return jsx("div", _objectSpread$1c(_objectSpread$1c({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-field', invalid && 'ams-field--invalid', className),
    children: children
  }));
});
Field.displayName = 'Field';

var _excluded$15 = ["children", "className"];
function ownKeys$1b(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1b(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1b(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1b(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SelectOption = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$15);
  return jsx("option", _objectSpread$1b(_objectSpread$1b({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-select__option', className),
    children: children
  }));
});
SelectOption.displayName = 'Select.Option';

var _excluded$14 = ["children", "className"];
function ownKeys$1a(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1a(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1a(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1a(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SelectOptionGroup = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$14);
  return jsx("optgroup", _objectSpread$1a(_objectSpread$1a({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-select__group', className),
    children: children
  }));
});
SelectOptionGroup.displayName = 'Select.Group';

var _excluded$13 = ["children", "className", "invalid"];
function ownKeys$19(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$19(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$19(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$19(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SelectRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    invalid = _ref.invalid,
    restProps = _objectWithoutProperties(_ref, _excluded$13);
  return jsx("select", _objectSpread$19(_objectSpread$19({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    className: clsx('ams-select', className),
    ref: ref,
    children: children
  }));
});
SelectRoot.displayName = 'Select';
var Select = Object.assign(SelectRoot, {
  Option: SelectOption,
  Group: SelectOptionGroup
});

var _excluded$12 = ["className", "invalid"];
function ownKeys$18(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$18(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$18(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$18(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TimeInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    invalid = _ref.invalid,
    restProps = _objectWithoutProperties(_ref, _excluded$12);
  return jsx("input", _objectSpread$18(_objectSpread$18({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    className: clsx('ams-time-input', className),
    ref: ref,
    type: "time"
  }));
});
TimeInput.displayName = 'TimeInput';

var _excluded$11 = ["className", "invalid", "type"];
function ownKeys$17(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$17(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$17(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$17(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DateInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    invalid = _ref.invalid,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'date' : _ref$type,
    restProps = _objectWithoutProperties(_ref, _excluded$11);
  return jsx("input", _objectSpread$17(_objectSpread$17({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    className: clsx('ams-date-input', className),
    ref: ref,
    type: type
  }));
});
DateInput.displayName = 'DateInput';

var _excluded$10 = ["label", "imageSrc", "className", "color"];
function ownKeys$16(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$16(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$16(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$16(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var AvatarContent = function AvatarContent(_ref) {
  var imageSrc = _ref.imageSrc,
    initials = _ref.initials;
  if (imageSrc) {
    return jsx(Image, {
      src: imageSrc,
      alt: ""
    });
  }
  if (initials.length) {
    return jsx("span", {
      "aria-hidden": true,
      children: initials
    });
  }
  return jsx(Icon, {
    svg: PersonalLoginIcon,
    size: "level-6"
  });
};
var Avatar = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var label = _ref2.label,
    imageSrc = _ref2.imageSrc,
    className = _ref2.className,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'blue' : _ref2$color,
    restProps = _objectWithoutProperties(_ref2, _excluded$10);
  var initials = label.slice(0, 2).toUpperCase();
  var a11yLabel = initials.length === 0 ? 'Gebruiker' : "Initialen gebruiker: ".concat(initials);
  return jsxs("span", _objectSpread$16(_objectSpread$16({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-avatar', "ams-avatar--".concat(color), imageSrc && 'ams-avatar--has-image', className),
    children: [jsx("span", {
      className: "ams-visually-hidden",
      children: a11yLabel
    }), jsx(AvatarContent, {
      imageSrc: imageSrc,
      initials: initials
    })]
  }));
});
Avatar.displayName = 'Avatar';

var _excluded$$ = ["className", "length", "maxLength"];
function ownKeys$15(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$15(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$15(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$15(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var CharacterCount = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    length = _ref.length,
    maxLength = _ref.maxLength,
    restProps = _objectWithoutProperties(_ref, _excluded$$);
  return jsx("div", _objectSpread$15(_objectSpread$15({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-character-count', length > maxLength && 'ams-character-count--error', className),
    role: "status",
    children: "".concat(length, " van ").concat(maxLength, " tekens")
  }));
});
CharacterCount.displayName = 'CharacterCount';

var _excluded$_ = ["children", "className"];
function ownKeys$14(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$14(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$14(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$14(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DescriptionListDescription = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$_);
  return jsx("dd", _objectSpread$14(_objectSpread$14({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-description-list__description', className),
    children: children
  }));
});
DescriptionListDescription.displayName = 'DescriptionList.Description';

var _excluded$Z = ["children", "className"];
function ownKeys$13(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$13(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$13(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$13(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DescriptionListTerm = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$Z);
  return jsx("dt", _objectSpread$13(_objectSpread$13({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-description-list__term', className),
    children: children
  }));
});
DescriptionListTerm.displayName = 'DescriptionList.Term';

var _excluded$Y = ["children", "className", "inverseColor", "termsWidth"];
function ownKeys$12(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$12(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$12(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$12(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DescriptionListRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    termsWidth = _ref.termsWidth,
    restProps = _objectWithoutProperties(_ref, _excluded$Y);
  return jsx("dl", _objectSpread$12(_objectSpread$12({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-description-list', termsWidth && "ams-description-list--terms-width-".concat(termsWidth), inverseColor && 'ams-description-list--inverse-color', className),
    children: children
  }));
});
DescriptionListRoot.displayName = 'DescriptionList';
var DescriptionList = Object.assign(DescriptionListRoot, {
  Term: DescriptionListTerm,
  Description: DescriptionListDescription
});

var _excluded$X = ["align", "alignVertical", "as", "children", "className", "gap", "wrap"];
function ownKeys$11(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$11(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$11(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$11(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Row = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var align = _ref.align,
    alignVertical = _ref.alignVertical,
    _ref$as = _ref.as,
    Tag = _ref$as === void 0 ? 'div' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    gap = _ref.gap,
    wrap = _ref.wrap,
    restProps = _objectWithoutProperties(_ref, _excluded$X);
  return jsx(Tag, _objectSpread$11(_objectSpread$11({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-row', align && "ams-row--align-".concat(align), alignVertical && "ams-row--align-vertical-".concat(alignVertical), gap && "ams-row--gap-".concat(gap), wrap && 'ams-row--wrap', className),
    children: children
  }));
});
Row.displayName = 'Row';

var _excluded$W = ["children", "className", "icon", "invalid"];
function ownKeys$10(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$10(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$10(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$10(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Radio = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    icon = _ref.icon,
    invalid = _ref.invalid,
    restProps = _objectWithoutProperties(_ref, _excluded$W);
  var id = useId();
  return (
    // This div is here because NVDA doesn't match the input to the label
    // without a containing element
    jsxs("div", {
      className: clsx('ams-radio', className),
      children: [jsx("input", _objectSpread$10(_objectSpread$10({}, restProps), {}, {
        "aria-invalid": invalid || undefined,
        className: "ams-radio__input",
        id: id,
        ref: ref,
        type: "radio"
      })), jsxs("label", {
        className: "ams-radio__label",
        htmlFor: id,
        children: [jsx("span", {
          className: "ams-radio__icon-container",
          children: icon !== null && icon !== void 0 ? icon : jsx(RadioIcon, {})
        }), children]
      })]
    })
  );
});
Radio.displayName = 'Radio';

/**
 * @license EUPL-1.2+
 * Copyright Gemeente Amsterdam
 */
var defaultValues$1 = {
  activeTab: 0,
  tabsId: '',
  updateTab: function updateTab() {}
};
var TabsContext = /*#__PURE__*/createContext(defaultValues$1);

var _excluded$V = ["children", "className", "tab"];
function ownKeys$$(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$$(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$$(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$$(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TabsButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$tab = _ref.tab,
    tab = _ref$tab === void 0 ? 0 : _ref$tab,
    restProps = _objectWithoutProperties(_ref, _excluded$V);
  var _useContext = useContext(TabsContext),
    activeTab = _useContext.activeTab,
    tabsId = _useContext.tabsId,
    updateTab = _useContext.updateTab;
  return jsx("button", _objectSpread$$(_objectSpread$$({}, restProps), {}, {
    "aria-controls": "".concat(tabsId, "-panel-").concat(tab),
    "aria-selected": activeTab === tab,
    className: clsx('ams-tabs__button', className),
    id: "".concat(tabsId, "-tab-").concat(tab),
    onClick: function onClick() {
      startTransition(function () {
        updateTab(tab);
      });
    },
    ref: ref,
    role: "tab",
    tabIndex: activeTab === tab ? 0 : -1,
    children: children
  }));
});
TabsButton.displayName = 'Tabs.Button';

var _excluded$U = ["children", "className"];
function ownKeys$_(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$_(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$_(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$_(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TabsList = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$U);
  return jsx("div", _objectSpread$_(_objectSpread$_({}, restProps), {}, {
    role: "tablist",
    ref: ref,
    className: clsx('ams-tabs__list', className),
    children: children
  }));
});
TabsList.displayName = 'Tabs.List';

var _excluded$T = ["tab", "children", "className"];
function ownKeys$Z(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$Z(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$Z(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$Z(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TabsPanel = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var tab = _ref.tab,
    children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$T);
  var _useContext = useContext(TabsContext),
    activeTab = _useContext.activeTab,
    tabsId = _useContext.tabsId;
  if (tab !== activeTab) {
    return null;
  }
  return jsx("div", _objectSpread$Z(_objectSpread$Z({}, restProps), {}, {
    role: "tabpanel",
    "aria-labelledby": "".concat(tabsId, "-tab-").concat(tab),
    id: "".concat(tabsId, "-panel-").concat(tab),
    tabIndex: 0,
    ref: ref,
    className: clsx('ams-tabs__panel', className),
    children: children
  }));
});
TabsPanel.displayName = 'Tabs.Panel';

/**
 * @license EUPL-1.2+
 * Copyright Gemeente Amsterdam
 */
var KeyboardKeys = {
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowRight: 'ArrowRight',
  ArrowLeft: 'ArrowLeft',
  Home: 'Home',
  End: 'End'
};
var FOCUSABLE_ELEMENTS = ['a[href]:not([disabled])', 'button:not([disabled])', 'textarea:not([disabled])', 'input[type="text"]:not([disabled])', 'input[type="radio"]:not([disabled])', 'input[type="checkbox"]:not([disabled])', 'select:not([disabled])'];
var useKeyboardFocus = function useKeyboardFocus(ref, options) {
  var _options$rotating = options.rotating,
    rotating = _options$rotating === void 0 ? false : _options$rotating,
    _options$directChildr = options.directChildrenOnly,
    directChildrenOnly = _options$directChildr === void 0 ? false : _options$directChildr,
    _options$horizontally = options.horizontally,
    horizontally = _options$horizontally === void 0 ? false : _options$horizontally;
  var next = horizontally ? KeyboardKeys.ArrowRight : KeyboardKeys.ArrowDown;
  var previous = horizontally ? KeyboardKeys.ArrowLeft : KeyboardKeys.ArrowUp;
  var keyDown = function keyDown(e) {
    if (ref.current) {
      var element = ref.current;
      var activeElement = window.document.activeElement;
      var directChildSelector = directChildrenOnly ? ':scope > ' : '';
      var focusableEls = Array.from(element.querySelectorAll("".concat(directChildSelector).concat(FOCUSABLE_ELEMENTS.join(", ".concat(directChildSelector)))));
      var getIndex = function getIndex(el) {
        return el && focusableEls.includes(el) ? focusableEls.indexOf(el) : 0;
      };
      var el;
      switch (e.key) {
        case next:
          {
            if (getIndex(activeElement) !== focusableEls.length - 1) {
              el = focusableEls[getIndex(activeElement) + 1];
              // If there is nothing focused yet, set the focus on the first element
              if (activeElement && !focusableEls.includes(activeElement)) {
                el = focusableEls[0];
              }
            } else if (rotating) {
              el = focusableEls[0];
            }
            break;
          }
        case previous:
          {
            if (getIndex(activeElement) !== 0) {
              el = focusableEls[getIndex(activeElement) - 1];
            } else if (rotating) {
              el = focusableEls[focusableEls.length - 1];
            }
            break;
          }
        case KeyboardKeys.Home:
          {
            el = focusableEls[0];
            break;
          }
        case KeyboardKeys.End:
          {
            el = focusableEls[focusableEls.length - 1];
            break;
          }
      }
      if ((e.key === KeyboardKeys.ArrowDown || e.key === KeyboardKeys.ArrowUp || e.key === KeyboardKeys.ArrowLeft || e.key === KeyboardKeys.ArrowRight || e.key === KeyboardKeys.Home || e.key === KeyboardKeys.End) && el instanceof HTMLElement) {
        el.focus();
        e.preventDefault();
      }
    }
  };
  return {
    keyDown: keyDown
  };
};

var _excluded$S = ["activeTab", "children", "className"];
function ownKeys$Y(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$Y(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$Y(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$Y(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TabsRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var activeTab = _ref.activeTab,
    children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$S);
  var tabsId = useId();
  var innerRef = useRef(null);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    activeTabId = _useState2[0],
    setActiveTabId = _useState2[1];
  var allTabs = useMemo(function () {
    if (!Array.isArray(children)) return [];
    return children[0].props.children.map(function (child) {
      return child;
    });
  }, [children]);
  useEffect(function () {
    if (typeof activeTab !== 'number') return;
    if (!Number.isInteger(activeTab)) return;
    if (activeTab < 0) {
      setActiveTabId(0);
    } else if (activeTab > allTabs.length - 1) {
      setActiveTabId(allTabs.length - 1);
    } else {
      setActiveTabId(activeTab);
    }
  }, [activeTab, allTabs]);
  var updateTab = function updateTab(tab) {
    setActiveTabId(tab);
  };
  // Use a passed ref if it's there, otherwise use innerRef
  useImperativeHandle(ref, function () {
    return innerRef.current;
  });
  var _useKeyboardFocus = useKeyboardFocus(innerRef, {
      rotating: true,
      horizontally: true
    }),
    keyDown = _useKeyboardFocus.keyDown;
  return jsx(TabsContext.Provider, {
    value: {
      activeTab: activeTabId,
      updateTab: updateTab,
      tabsId: tabsId
    },
    children: jsx("div", _objectSpread$Y(_objectSpread$Y({}, restProps), {}, {
      role: "tabs",
      ref: innerRef,
      onKeyDown: keyDown,
      className: clsx('ams-tabs', className),
      children: children
    }))
  });
});
TabsRoot.displayName = 'Tabs';
var Tabs = Object.assign(TabsRoot, {
  Button: TabsButton,
  List: TabsList,
  Panel: TabsPanel
});

var _excluded$R = ["className", "dir", "invalid", "resize"];
function ownKeys$X(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$X(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$X(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$X(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TextArea = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    dir = _ref.dir,
    invalid = _ref.invalid,
    resize = _ref.resize,
    restProps = _objectWithoutProperties(_ref, _excluded$R);
  return jsx("textarea", _objectSpread$X(_objectSpread$X({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    className: clsx('ams-text-area', resize && "ams-text-area--resize-".concat(resize), restProps.cols && 'ams-text-area--cols', className),
    dir: dir !== null && dir !== void 0 ? dir : 'auto',
    ref: ref
  }));
});
TextArea.displayName = 'TextArea';

var _excluded$Q = ["align", "alignHorizontal", "as", "children", "className", "gap"];
function ownKeys$W(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$W(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$W(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$W(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Column = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var align = _ref.align,
    alignHorizontal = _ref.alignHorizontal,
    _ref$as = _ref.as,
    Tag = _ref$as === void 0 ? 'div' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    gap = _ref.gap,
    restProps = _objectWithoutProperties(_ref, _excluded$Q);
  return jsx(Tag, _objectSpread$W(_objectSpread$W({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-column', align && "ams-column--align-".concat(align), alignHorizontal && "ams-column--align-horizontal-".concat(alignHorizontal), gap && "ams-column--gap-".concat(gap), className),
    children: children
  }));
});
Column.displayName = 'Column';

var _excluded$P = ["children", "className", "hint", "invalid", "legend", "optional"];
function ownKeys$V(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$V(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$V(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$V(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FieldSet = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    hint = _ref.hint,
    invalid = _ref.invalid,
    legend = _ref.legend,
    optional = _ref.optional,
    restProps = _objectWithoutProperties(_ref, _excluded$P);
  return jsxs("fieldset", _objectSpread$V(_objectSpread$V({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-field-set', invalid && 'ams-field-set--invalid', className),
    children: [jsxs("legend", {
      className: "ams-field-set__legend",
      children: [legend, " ", jsx(Hint, {
        hint: hint,
        optional: optional
      })]
    }), children]
  }));
});
FieldSet.displayName = 'FieldSet';

var _excluded$O = ["label", "className", "color"];
function ownKeys$U(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$U(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$U(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$U(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Badge = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var label = _ref.label,
    className = _ref.className,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'dark-green' : _ref$color,
    restProps = _objectWithoutProperties(_ref, _excluded$O);
  return jsx("span", _objectSpread$U(_objectSpread$U({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-badge', "ams-badge--".concat(color), className),
    children: label
  }));
});
Badge.displayName = 'Badge';

var _excluded$N = ["children", "className"];
function ownKeys$T(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$T(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$T(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$T(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableBody = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$N);
  return jsx("tbody", _objectSpread$T(_objectSpread$T({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__body', className),
    children: children
  }));
});
TableBody.displayName = 'Table.Body';

var _excluded$M = ["children", "className"];
function ownKeys$S(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$S(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$S(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$S(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableCaption = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$M);
  return jsx("caption", _objectSpread$S(_objectSpread$S({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__caption', className),
    children: children
  }));
});
TableCaption.displayName = 'Table.Caption';

var _excluded$L = ["children", "className"];
function ownKeys$R(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$R(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$R(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$R(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableCell = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$L);
  return jsx("td", _objectSpread$R(_objectSpread$R({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__cell', className),
    children: children
  }));
});
TableCell.displayName = 'Table.Cell';

var _excluded$K = ["children", "className"];
function ownKeys$Q(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$Q(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$Q(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$Q(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableFooter = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$K);
  return jsx("tfoot", _objectSpread$Q(_objectSpread$Q({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__footer', className),
    children: children
  }));
});
TableFooter.displayName = 'Table.Footer';

var _excluded$J = ["children", "className"];
function ownKeys$P(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$P(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$P(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$P(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableHeader = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$J);
  return jsx("thead", _objectSpread$P(_objectSpread$P({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__header', className),
    children: children
  }));
});
TableHeader.displayName = 'Table.Header';

var _excluded$I = ["children", "className"];
function ownKeys$O(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$O(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$O(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$O(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableHeaderCell = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$I);
  return jsx("th", _objectSpread$O(_objectSpread$O({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__header-cell', className),
    children: children
  }));
});
TableHeaderCell.displayName = 'Table.HeaderCell';

var _excluded$H = ["children", "className"];
function ownKeys$N(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$N(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$N(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$N(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableRow = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$H);
  return jsx("tr", _objectSpread$N(_objectSpread$N({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-table__row', className),
    children: children
  }));
});
TableRow.displayName = 'Table.Row';

var _excluded$G = ["children", "className"];
function ownKeys$M(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$M(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$M(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$M(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TableRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$G);
  return jsx("div", {
    className: "ams-table",
    children: jsx("table", _objectSpread$M(_objectSpread$M({}, restProps), {}, {
      ref: ref,
      className: clsx('ams-table__table', className),
      children: children
    }))
  });
});
TableRoot.displayName = 'Table';
var Table = Object.assign(TableRoot, {
  Body: TableBody,
  Caption: TableCaption,
  Cell: TableCell,
  Footer: TableFooter,
  Header: TableHeader,
  HeaderCell: TableHeaderCell,
  Row: TableRow
});

var _excluded$F = ["children", "className"];
function ownKeys$L(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$L(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$L(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$L(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var MegaMenuListCategory = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$F);
  return jsx("div", _objectSpread$L(_objectSpread$L({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-mega-menu__list-category', className),
    children: children
  }));
});
MegaMenuListCategory.displayName = 'MegaMenu.ListCategory';

var _excluded$E = ["children", "className"];
function ownKeys$K(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$K(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$K(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$K(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var MegaMenuRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$E);
  return jsx("div", _objectSpread$K(_objectSpread$K({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-mega-menu', className),
    children: children
  }));
});
MegaMenuRoot.displayName = 'MegaMenu';
var MegaMenu = Object.assign(MegaMenuRoot, {
  ListCategory: MegaMenuListCategory
});

var _excluded$D = ["children", "className"];
function ownKeys$J(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$J(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$J(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$J(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SkipLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$D);
  return jsx("a", _objectSpread$J(_objectSpread$J({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-skip-link', 'ams-visually-hidden', className),
    children: children
  }));
});
SkipLink.displayName = 'SkipLink';

var _excluded$C = ["children", "className"];
function ownKeys$I(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$I(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$I(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$I(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Overlap = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$C);
  return jsx("div", _objectSpread$I(_objectSpread$I({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-overlap', className),
    children: children
  }));
});
Overlap.displayName = 'Overlap';

function ownKeys$H(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$H(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$H(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$H(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoAmsterdam = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$H(_objectSpread$H({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 115 40",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__text-primary",
      fill: "#EC0000",
      d: "M16.435 8.581c0-3.752 2.63-6.4 6.344-6.4 1.886 0 3.466.61 4.59 1.829L25.731 5.57c-.704-.78-1.714-1.238-2.915-1.238-2.248 0-3.81 1.752-3.81 4.248 0 2.496 1.524 4.324 3.715 4.324 2 0 3.391-1.143 3.391-2.8v-.323h-2.99V7.705h5.505V9.63c0 3.162-2.457 5.39-5.981 5.39s-6.21-2.666-6.21-6.438ZM30.225 10.086c0-2.915 1.962-4.934 4.762-4.934s4.743 2 4.743 4.876c0 .229-.037.59-.076.839h-7.048C32.778 12.143 33.863 13 35.254 13c1.029 0 1.924-.381 2.514-1.067l1.428 1.391c-.857 1.106-2.343 1.696-4.057 1.696-2.895 0-4.915-2.038-4.915-4.934h.001Zm2.381-.952h4.743c-.152-1.238-1.105-2.077-2.38-2.077-1.276 0-2.229.839-2.363 2.077ZM43.902 5.382v1.275h.038c.476-.97 1.314-1.505 2.4-1.505 1.219 0 2.132.534 2.704 1.6.571-1.028 1.62-1.6 2.952-1.6 2.324 0 3.448 1.296 3.448 3.943v5.696h-2.362V9.495c0-1.543-.534-2.286-1.638-2.286-1.104 0-1.696.82-1.696 2.362v5.22h-2.362V9.495c0-1.562-.514-2.286-1.638-2.286-1.124 0-1.695.838-1.695 2.362v5.22H41.69V5.38h2.212ZM57.312 10.086c0-2.915 1.963-4.934 4.762-4.934 2.8 0 4.744 2 4.744 4.876 0 .229-.038.59-.077.839h-7.048C59.865 12.143 60.95 13 62.341 13c1.03 0 1.924-.381 2.514-1.067l1.429 1.391c-.857 1.106-2.344 1.696-4.058 1.696-2.895 0-4.915-2.038-4.915-4.934h.001Zm2.381-.952h4.743c-.152-1.238-1.105-2.077-2.38-2.077-1.276 0-2.229.839-2.363 2.077ZM68.189 10.086c0-2.915 1.962-4.934 4.762-4.934s4.743 2 4.743 4.876c0 .229-.038.59-.077.839H70.57C70.74 12.143 71.827 13 73.218 13c1.028 0 1.923-.381 2.513-1.067l1.429 1.391c-.857 1.106-2.343 1.696-4.058 1.696-2.895 0-4.915-2.038-4.915-4.934h.002Zm2.38-.952h4.744c-.153-1.238-1.105-2.077-2.381-2.077-1.276 0-2.228.839-2.362 2.077ZM81.883 5.382v1.275h.038c.438-.933 1.39-1.505 2.495-1.505 2.362 0 3.582 1.353 3.582 3.943v5.696h-2.363V9.552c0-1.6-.571-2.343-1.772-2.343-1.2 0-1.847.838-1.847 2.4v5.18h-2.362V5.382h2.23ZM91.026 11.42V7.342h-1.619V5.381h.858c.78 0 1.085-.305 1.085-1.086V2.83h2.019v2.553h2.285v1.962H93.37v4.077c0 .915.476 1.428 1.352 1.428.267 0 .59-.037.78-.095v1.981c-.285.077-.819.133-1.238.133-2.133 0-3.238-1.18-3.238-3.447l.001-.002ZM96.817 10.086c0-2.915 1.963-4.934 4.762-4.934 2.8 0 4.744 2 4.744 4.876 0 .229-.038.59-.077.839h-7.048c.172 1.276 1.257 2.133 2.648 2.133 1.029 0 1.924-.381 2.514-1.067l1.429 1.391c-.857 1.106-2.344 1.696-4.058 1.696-2.895 0-4.915-2.038-4.915-4.934h.001Zm2.381-.952h4.744c-.153-1.238-1.106-2.077-2.382-2.077-1.275 0-2.228.839-2.362 2.077ZM23.73 25.267l4.952 12.381h-2.647l-1.03-2.724H19.5l-1.028 2.724h-2.553l4.953-12.38h2.858Zm-3.486 7.6h4l-1.982-5.219h-.037l-1.981 5.22ZM32.454 28.24v1.275h.038c.476-.97 1.315-1.505 2.4-1.505 1.22 0 2.133.534 2.704 1.6.572-1.028 1.62-1.6 2.953-1.6 2.323 0 3.447 1.296 3.447 3.944v5.696h-2.362v-5.296c0-1.543-.534-2.286-1.638-2.286-1.104 0-1.695.82-1.695 2.362v5.22h-2.363v-5.296c0-1.562-.514-2.286-1.638-2.286-1.124 0-1.695.838-1.695 2.362v5.22h-2.362v-9.41h2.211ZM48.34 33.706c-1.275-.381-2.513-1.067-2.513-2.78 0-1.715 1.447-2.916 3.6-2.916 1.448 0 2.572.514 3.2 1.466l-1.351 1.353c-.477-.59-1.124-.915-1.829-.915-.8 0-1.296.362-1.296.933 0 .762.8.895 1.963 1.257 1.276.381 2.514 1.067 2.514 2.781 0 1.715-1.524 2.99-3.772 2.99-1.466 0-2.648-.514-3.334-1.486l1.353-1.352c.513.609 1.2.934 1.962.934.915 0 1.466-.381 1.466-1.01 0-.762-.8-.895-1.962-1.258v.003ZM55.122 34.277v-4.076h-1.62v-1.963h.858c.78 0 1.085-.304 1.085-1.085v-1.466h2.019v2.552h2.286v1.963h-2.286v4.076c0 .915.476 1.43 1.352 1.43.267 0 .59-.038.78-.096v1.981c-.285.077-.819.133-1.237.133-2.133 0-3.238-1.18-3.238-3.447v-.002ZM60.911 32.944c0-2.915 1.963-4.934 4.763-4.934s4.743 2 4.743 4.876c0 .228-.038.59-.077.839h-7.048c.172 1.276 1.257 2.133 2.648 2.133 1.03 0 1.924-.381 2.514-1.067l1.429 1.391c-.857 1.105-2.343 1.696-4.058 1.696-2.895 0-4.915-2.038-4.915-4.934h.001Zm2.381-.952h4.744c-.153-1.239-1.106-2.077-2.381-2.077-1.276 0-2.229.838-2.363 2.077ZM74.607 28.24V29.8h.038c.457-1.066 1.296-1.638 2.4-1.638.285 0 .685.038.838.095v2.248c-.267-.076-.78-.132-1.162-.132-1.2 0-1.98.914-1.98 2.323v4.953h-2.363v-9.41h2.23ZM78.551 32.983c0-2.915 1.924-4.972 4.648-4.972 1.257 0 2.286.553 2.934 1.562h.037V23.84h2.362v13.81h-2.228v-1.467h-.037c-.61 1.067-1.752 1.696-3.067 1.696-2.724 0-4.648-2.038-4.648-4.895l-.001-.001Zm7.62-.038c0-1.695-1.048-2.82-2.591-2.82-1.543 0-2.61 1.143-2.61 2.82 0 1.677 1.049 2.82 2.61 2.82s2.59-1.124 2.59-2.82ZM90.34 34.963c0-1.81 1.506-2.895 4.02-2.915l1.828-.019v-.304c0-1.106-.704-1.677-1.981-1.677-.838 0-1.581.323-2.095.915l-1.429-1.391c.82-1.01 2.133-1.562 3.752-1.562 2.514 0 4.114 1.371 4.114 3.562v3.733c0 .343.153.495.534.495h.172v1.847H98.15c-.895 0-1.506-.418-1.696-1.105h-.038c-.59.857-1.561 1.333-2.743 1.333-1.981 0-3.333-1.18-3.333-2.914v.002Zm5.848-.82v-.534l-1.561.02c-1.238.018-1.905.457-1.905 1.275 0 .724.571 1.238 1.37 1.238 1.239 0 2.096-.82 2.096-2v.001ZM103.312 28.24v1.275h.038c.476-.97 1.315-1.505 2.4-1.505 1.219 0 2.133.534 2.704 1.6.572-1.028 1.619-1.6 2.953-1.6 2.323 0 3.447 1.296 3.447 3.944v5.696h-2.362v-5.296c0-1.543-.534-2.286-1.638-2.286-1.104 0-1.695.82-1.695 2.362v5.22h-2.363v-5.296c0-1.562-.513-2.286-1.638-2.286-1.124 0-1.695.838-1.695 2.362v5.22h-2.362v-9.41h2.211Z"
    }), jsx("path", {
      className: "ams-logo__emblem",
      fill: "#EC0000",
      d: "m0 37.648 3.527-3.527L0 30.593l2.352-2.351 3.527 3.527 3.528-3.527 2.351 2.351-3.527 3.528 3.527 3.527L9.407 40l-3.528-3.527L2.352 40 0 37.648ZM0 23.54l3.527-3.528L0 16.485l2.352-2.352 3.527 3.528 3.528-3.528 2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.527 3.527L0 23.54ZM0 9.407l3.527-3.528L0 2.352 2.352 0l3.527 3.527L9.407 0l2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.526 3.527L0 9.407Z"
    })]
  }));
});
LogoAmsterdam.displayName = 'LogoAmsterdam';

function ownKeys$G(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$G(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$G(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$G(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoGgdAmsterdam = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$G(_objectSpread$G({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 115 40",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__emblem",
      fill: "#EC0000",
      d: "m0 37.648 3.527-3.527L0 30.593l2.352-2.351 3.527 3.527 3.528-3.527 2.351 2.351-3.527 3.528 3.527 3.527L9.407 40l-3.528-3.527L2.352 40 0 37.648ZM0 23.54l3.527-3.528L0 16.485l2.352-2.352 3.527 3.528 3.528-3.528 2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.527 3.527L0 23.54ZM0 9.407l3.527-3.528L0 2.352 2.352 0l3.527 3.527L9.407 0l2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.526 3.527L0 9.407Z"
    }), jsx("path", {
      className: "ams-logo__text-primary",
      fill: "#EC0000",
      d: "m23.73 25.267 4.952 12.381h-2.647l-1.03-2.724H19.5l-1.028 2.724h-2.553l4.953-12.381h2.858Zm-3.486 7.6h4l-1.982-5.219h-.037l-1.981 5.22ZM32.454 28.24v1.275h.038c.476-.971 1.315-1.505 2.4-1.505 1.22 0 2.133.534 2.704 1.6.572-1.028 1.62-1.6 2.953-1.6 2.323 0 3.447 1.296 3.447 3.944v5.696h-2.362v-5.296c0-1.543-.534-2.286-1.638-2.286-1.104 0-1.695.82-1.695 2.362v5.22h-2.363v-5.296c0-1.562-.514-2.286-1.638-2.286-1.124 0-1.695.838-1.695 2.362v5.22h-2.362v-9.41h2.211ZM48.34 33.706c-1.275-.381-2.513-1.067-2.513-2.78 0-1.715 1.447-2.916 3.6-2.916 1.448 0 2.572.514 3.2 1.466l-1.351 1.353c-.477-.59-1.124-.915-1.829-.915-.8 0-1.296.362-1.296.933 0 .762.8.895 1.963 1.257 1.276.381 2.514 1.067 2.514 2.781 0 1.715-1.524 2.99-3.772 2.99-1.466 0-2.648-.514-3.334-1.486l1.353-1.352c.513.609 1.2.934 1.962.934.915 0 1.466-.381 1.466-1.01 0-.762-.8-.895-1.962-1.258v.003ZM55.122 34.277v-4.076h-1.62v-1.963h.858c.78 0 1.085-.304 1.085-1.085v-1.466h2.019v2.552h2.286v1.963h-2.286v4.076c0 .915.476 1.43 1.352 1.43.267 0 .59-.038.78-.096v1.981c-.285.076-.819.133-1.237.133-2.133 0-3.238-1.18-3.238-3.447v-.002ZM60.911 32.944c0-2.915 1.963-4.934 4.763-4.934s4.743 2 4.743 4.876c0 .228-.038.59-.077.839h-7.048c.172 1.275 1.257 2.133 2.648 2.133 1.03 0 1.924-.381 2.514-1.067l1.429 1.391c-.857 1.105-2.343 1.696-4.058 1.696-2.895 0-4.915-2.038-4.915-4.934h.001Zm2.381-.953h4.744c-.153-1.238-1.106-2.076-2.381-2.076-1.276 0-2.229.838-2.363 2.076ZM74.607 28.24V29.8h.038c.457-1.066 1.296-1.638 2.4-1.638.285 0 .685.038.838.095v2.248c-.267-.076-.78-.132-1.162-.132-1.2 0-1.98.914-1.98 2.323v4.953h-2.363v-9.41h2.23ZM78.551 32.983c0-2.915 1.924-4.972 4.648-4.972 1.257 0 2.286.553 2.934 1.562h.037V23.84h2.362v13.81h-2.228v-1.467h-.037c-.61 1.067-1.752 1.696-3.067 1.696-2.724 0-4.648-2.038-4.648-4.895l-.001-.001Zm7.62-.038c0-1.695-1.048-2.82-2.591-2.82-1.543 0-2.61 1.143-2.61 2.82 0 1.677 1.049 2.82 2.61 2.82s2.59-1.124 2.59-2.82ZM90.34 34.963c0-1.81 1.506-2.895 4.02-2.915l1.828-.019v-.304c0-1.106-.704-1.677-1.981-1.677-.838 0-1.581.323-2.095.915l-1.429-1.391c.82-1.01 2.133-1.562 3.752-1.562 2.514 0 4.114 1.371 4.114 3.562v3.733c0 .343.153.495.534.495h.172v1.847H98.15c-.895 0-1.506-.418-1.696-1.105h-.038c-.59.857-1.561 1.333-2.743 1.333-1.981 0-3.333-1.18-3.333-2.915v.003Zm5.848-.82v-.534l-1.561.02c-1.238.018-1.905.457-1.905 1.275 0 .724.571 1.238 1.37 1.238 1.239 0 2.096-.82 2.096-2v.001ZM103.312 28.24v1.275h.038c.476-.971 1.315-1.505 2.4-1.505 1.219 0 2.133.534 2.704 1.6.572-1.028 1.619-1.6 2.953-1.6 2.323 0 3.447 1.296 3.447 3.944v5.696h-2.362v-5.296c0-1.543-.534-2.286-1.638-2.286-1.104 0-1.695.82-1.695 2.362v5.22h-2.363v-5.296c0-1.562-.513-2.286-1.638-2.286-1.124 0-1.695.838-1.695 2.362v5.22h-2.362v-9.41h2.211Z"
    }), jsx("path", {
      className: "ams-logo__text-secondary",
      fill: "#000",
      d: "M16.436 8.581c0-3.752 2.629-6.4 6.343-6.4 1.886 0 3.466.61 4.59 1.829L25.732 5.57c-.705-.78-1.715-1.238-2.915-1.238-2.248 0-3.81 1.752-3.81 4.248 0 2.496 1.524 4.324 3.714 4.324 2 0 3.392-1.143 3.392-2.8v-.323h-2.99V7.705h5.505V9.63c0 3.162-2.458 5.39-5.982 5.39s-6.21-2.666-6.21-6.438ZM30.34 8.581c0-3.752 2.63-6.4 6.344-6.4 1.886 0 3.467.61 4.59 1.829L39.638 5.57c-.705-.78-1.715-1.238-2.915-1.238-2.248 0-3.81 1.752-3.81 4.248 0 2.496 1.524 4.324 3.715 4.324 2 0 3.39-1.143 3.39-2.8v-.323h-2.99V7.705h5.506V9.63c0 3.162-2.458 5.39-5.982 5.39s-6.21-2.666-6.21-6.438ZM44.836 2.41h4.61c3.809 0 6.343 2.476 6.343 6.19 0 3.714-2.59 6.19-6.363 6.19h-4.59V2.41Zm4.419 10.229c2.38 0 3.942-1.543 3.942-4.02 0-2.475-1.524-4.057-3.923-4.057H47.35v8.077h1.905Z"
    })]
  }));
});
LogoGgdAmsterdam.displayName = 'LogoGgdAmsterdam';

function ownKeys$F(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$F(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$F(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$F(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoMuseumWeesp = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$F(_objectSpread$F({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 121 42",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__text-secondary",
      d: "M18.085 30.527H20.669L23.051 35.26H23.08L25.46 30.527H28.045V39.964H26.187V32.69H26.157L23.82 37.336H22.31L19.973 32.69H19.943V39.964H18.085V30.527ZM29.976 37.133V32.792H31.776V36.799C31.776 38.004 32.197 38.57 33.068 38.57C33.954 38.57 34.448 37.917 34.448 36.755V32.792H36.248V39.964H34.55V38.991H34.521C34.173 39.688 33.418 40.138 32.605 40.138C30.848 40.138 29.976 39.137 29.976 37.133ZM39.63 36.959C38.658 36.669 37.714 36.146 37.714 34.839C37.714 33.518 38.818 32.618 40.458 32.618C41.562 32.618 42.418 33.009 42.898 33.735L41.867 34.766C41.504 34.316 41.01 34.069 40.473 34.069C39.863 34.069 39.486 34.345 39.486 34.781C39.486 35.361 40.096 35.463 40.981 35.739C41.954 36.029 42.898 36.552 42.898 37.859C42.898 39.209 41.736 40.139 40.023 40.139C38.905 40.139 38.005 39.746 37.482 39.006L38.512 37.975C38.6925 38.1976 38.9205 38.3771 39.1794 38.5001C39.4383 38.6231 39.7214 38.6866 40.008 38.686C40.705 38.686 41.126 38.396 41.126 37.917C41.126 37.337 40.515 37.235 39.63 36.959ZM45.7 35.652H49.315C49.199 34.708 48.473 34.069 47.5 34.069C46.527 34.069 45.801 34.709 45.7 35.652ZM43.885 36.378C43.885 34.157 45.38 32.618 47.515 32.618C49.649 32.618 51.13 34.142 51.13 36.334C51.13 36.509 51.1 36.784 51.072 36.974H45.699C45.83 37.946 46.658 38.599 47.718 38.599C48.502 38.599 49.184 38.309 49.634 37.786L50.724 38.846C50.07 39.688 48.937 40.138 47.631 40.138C45.424 40.138 43.885 38.585 43.885 36.378ZM52.523 37.133V32.792H54.323V36.799C54.323 38.004 54.745 38.57 55.616 38.57C56.502 38.57 56.996 37.917 56.996 36.755V32.792H58.796V39.964H57.096V38.991H57.068C56.72 39.688 55.964 40.138 55.151 40.138C53.395 40.138 52.523 39.137 52.523 37.133ZM60.741 32.792H62.425V33.765H62.455C62.817 33.024 63.456 32.618 64.284 32.618C65.213 32.618 65.91 33.024 66.345 33.838C66.781 33.053 67.58 32.618 68.595 32.618C70.367 32.618 71.224 33.605 71.224 35.623V39.964H69.424V35.928C69.424 34.752 69.017 34.186 68.174 34.186C67.333 34.186 66.883 34.81 66.883 35.986V39.964H65.083V35.928C65.083 34.738 64.69 34.186 63.833 34.186C63.006 34.186 62.541 34.824 62.541 35.986V39.964H60.741V32.792ZM84.683 39.964L82.853 32.908H82.824L80.994 39.964H78.774L76 30.527H78.033L79.891 37.67H79.921L81.793 30.527H83.884L85.757 37.67H85.787L87.645 30.527H89.677L86.904 39.964H84.683ZM91.71 35.652H95.325C95.209 34.708 94.483 34.069 93.51 34.069C92.537 34.069 91.812 34.709 91.71 35.652ZM89.895 36.378C89.895 34.157 91.39 32.618 93.525 32.618C95.659 32.618 97.14 34.142 97.14 36.334C97.14 36.509 97.11 36.784 97.082 36.974H91.71C91.84 37.946 92.668 38.599 93.728 38.599C94.512 38.599 95.194 38.309 95.645 37.786L96.733 38.846C96.08 39.688 94.948 40.138 93.641 40.138C91.434 40.138 89.895 38.585 89.895 36.378ZM100 35.652H103.616C103.499 34.708 102.773 34.069 101.801 34.069C100.828 34.069 100.1 34.709 100 35.652ZM98.185 36.378C98.185 34.157 99.681 32.618 101.815 32.618C103.949 32.618 105.43 34.142 105.43 36.334C105.43 36.509 105.401 36.784 105.372 36.974H100C100.13 37.946 100.958 38.599 102.018 38.599C102.802 38.599 103.485 38.309 103.935 37.786L105.024 38.846C104.37 39.688 103.238 40.138 101.931 40.138C99.724 40.138 98.185 38.585 98.185 36.378ZM108.363 36.959C107.39 36.669 106.446 36.146 106.446 34.839C106.446 33.518 107.55 32.618 109.191 32.618C110.294 32.618 111.151 33.009 111.63 33.735L110.599 34.766C110.236 34.316 109.742 34.069 109.205 34.069C108.595 34.069 108.218 34.345 108.218 34.781C108.218 35.361 108.828 35.463 109.713 35.739C110.686 36.029 111.63 36.552 111.63 37.859C111.63 39.209 110.468 40.139 108.755 40.139C107.637 40.139 106.737 39.746 106.215 39.006L107.245 37.975C107.426 38.1976 107.654 38.3771 107.912 38.5001C108.171 38.6231 108.454 38.6866 108.741 38.686C109.437 38.686 109.859 38.396 109.859 37.917C109.859 37.337 109.249 37.235 108.363 36.959ZM118.83 36.378C118.83 35.086 118.033 34.229 116.857 34.229C115.68 34.229 114.867 35.1 114.867 36.378C114.867 37.656 115.68 38.527 116.857 38.527C118.033 38.527 118.831 37.67 118.831 36.377L118.83 36.378ZM113.067 32.792H114.78V33.91H114.81C115.245 33.097 116.116 32.618 117.162 32.618C119.209 32.618 120.675 34.171 120.675 36.348C120.675 38.556 119.209 40.138 117.162 40.138C116.189 40.138 115.39 39.718 114.896 38.948H114.867V42.65H113.067V32.792Z",
      fill: "#000"
    }), jsx("path", {
      className: "ams-logo__text-primary",
      d: "M20.539 22.287H23.588L22.078 18.309H22.048L20.539 22.287ZM21.018 16.494H23.196L26.971 25.931H24.953L24.169 23.855H19.973L19.189 25.931H17.243L21.018 16.494ZM28.161 18.759H29.845V19.732H29.875C30.237 18.992 30.876 18.584 31.704 18.584C32.633 18.584 33.33 18.991 33.765 19.804C34.201 19.02 35 18.584 36.015 18.584C37.787 18.584 38.644 19.572 38.644 21.59V25.931H36.844V21.895C36.844 20.719 36.437 20.152 35.594 20.152C34.753 20.152 34.303 20.777 34.303 21.952V25.932H32.503V21.895C32.503 20.705 32.11 20.152 31.253 20.152C30.426 20.152 29.961 20.792 29.961 21.952V25.932H28.161V18.759ZM41.954 22.926C40.981 22.636 40.037 22.113 40.037 20.806C40.037 19.485 41.141 18.584 42.781 18.584C43.885 18.584 44.741 18.977 45.221 19.702L44.19 20.733C43.827 20.283 43.333 20.036 42.796 20.036C42.186 20.036 41.809 20.312 41.809 20.748C41.809 21.328 42.419 21.43 43.304 21.706C44.277 21.996 45.221 22.519 45.221 23.826C45.221 25.176 44.059 26.106 42.346 26.106C41.228 26.106 40.328 25.713 39.806 24.973L40.836 23.942C41.0164 24.1645 41.2443 24.3439 41.5029 24.4669C41.7616 24.5899 42.0446 24.6535 42.331 24.653C43.028 24.653 43.449 24.363 43.449 23.884C43.449 23.304 42.839 23.202 41.954 22.926ZM47.122 23.361V20.254H45.888V18.76H46.542C47.137 18.76 47.369 18.527 47.369 17.932V16.814H48.909V18.76H50.651V20.255H48.908V23.362C48.908 24.059 49.271 24.452 49.939 24.452C50.142 24.452 50.389 24.422 50.534 24.379V25.889C50.2243 25.9569 49.9081 25.9907 49.591 25.99C47.965 25.99 47.122 25.089 47.122 23.361ZM53.351 21.619H56.966C56.85 20.675 56.124 20.036 55.151 20.036C54.179 20.036 53.453 20.676 53.351 21.619ZM51.536 22.345C51.536 20.124 53.032 18.585 55.166 18.585C57.3 18.585 58.781 20.109 58.781 22.301C58.781 22.476 58.752 22.751 58.723 22.941H53.351C53.481 23.913 54.309 24.566 55.369 24.566C56.153 24.566 56.836 24.276 57.286 23.753L58.375 24.813C57.721 25.655 56.589 26.105 55.282 26.105C53.075 26.105 51.536 24.552 51.536 22.345ZM60.277 18.759H61.975V19.949H62.005C62.353 19.136 62.992 18.701 63.834 18.701C64.051 18.701 64.356 18.73 64.472 18.773V20.487C64.1819 20.4194 63.8849 20.3852 63.587 20.385C62.672 20.385 62.077 21.082 62.077 22.156V25.931H60.277V18.759ZM70.788 22.345C70.788 21.053 69.99 20.196 68.814 20.196C67.623 20.196 66.824 21.067 66.824 22.346C66.824 23.623 67.623 24.494 68.814 24.494C69.99 24.494 70.788 23.637 70.788 22.345ZM64.981 22.374C64.981 20.152 66.447 18.584 68.523 18.584C69.481 18.584 70.265 19.006 70.759 19.775H70.789V15.405H72.589V25.93H70.889V24.812H70.861C70.396 25.625 69.525 26.104 68.523 26.104C66.447 26.104 64.981 24.552 64.981 22.374ZM78.425 23.26V22.853L77.235 22.868C76.291 22.882 75.783 23.216 75.783 23.841C75.783 24.392 76.218 24.784 76.828 24.784C77.771 24.784 78.425 24.16 78.425 23.26ZM73.968 23.884C73.968 22.504 75.115 21.677 77.031 21.663L78.425 21.648V21.416C78.425 20.574 77.888 20.138 76.915 20.138C76.276 20.138 75.71 20.385 75.318 20.835L74.229 19.775C74.853 19.005 75.855 18.585 77.089 18.585C79.006 18.585 80.225 19.63 80.225 21.3V24.145C80.225 24.407 80.341 24.523 80.632 24.523H80.762V25.931H79.92C79.238 25.931 78.773 25.611 78.628 25.089H78.6C78.15 25.743 77.41 26.105 76.51 26.105C75 26.105 73.969 25.205 73.969 23.885L73.968 23.884ZM82.17 18.759H83.855V19.732H83.884C84.247 18.992 84.886 18.584 85.714 18.584C86.642 18.584 87.339 18.991 87.774 19.804C88.21 19.02 89.009 18.584 90.025 18.584C91.797 18.584 92.653 19.572 92.653 21.59V25.931H90.853V21.895C90.853 20.719 90.446 20.152 89.604 20.152C88.762 20.152 88.312 20.777 88.312 21.952V25.932H86.512V21.895C86.512 20.705 86.12 20.152 85.263 20.152C84.436 20.152 83.971 20.792 83.971 21.952V25.932H82.171L82.17 18.759ZM17.644 6.97998C17.644 4.11998 19.647 2.09998 22.479 2.09998C23.916 2.09998 25.121 2.56498 25.978 3.49398L24.729 4.68398C24.192 4.08898 23.422 3.74098 22.508 3.74098C20.795 3.74098 19.604 5.07698 19.604 6.97898C19.604 8.92398 20.765 10.274 22.435 10.274C23.96 10.274 25.02 9.40398 25.02 8.13998V7.89298H22.74V6.31198H26.936V7.77798C26.936 10.188 25.063 11.887 22.377 11.887C19.604 11.887 17.644 9.85398 17.644 6.97998ZM29.97 7.39998H33.586C33.469 6.45698 32.743 5.81798 31.771 5.81798C30.798 5.81798 30.072 6.45698 29.971 7.40098L29.97 7.39998ZM28.155 8.12698C28.155 5.90498 29.651 4.36698 31.785 4.36698C33.92 4.36698 35.4 5.89098 35.4 8.08298C35.4 8.25698 35.372 8.53298 35.342 8.72198H29.97C30.101 9.69498 30.929 10.348 31.988 10.348C32.773 10.348 33.455 10.058 33.905 9.53498L34.994 10.595C34.341 11.437 33.208 11.887 31.901 11.887C29.694 11.887 28.155 10.333 28.155 8.12698ZM36.896 4.53998H38.58V5.51298H38.609C38.972 4.77298 39.611 4.36598 40.439 4.36598C41.368 4.36598 42.065 4.77298 42.5 5.58598C42.936 4.80198 43.734 4.36598 44.75 4.36598C46.522 4.36598 47.379 5.35398 47.379 7.37198V11.712H45.579V7.67698C45.579 6.50198 45.172 5.93498 44.329 5.93498C43.488 5.93498 43.037 6.55998 43.037 7.73498V11.714H41.237V7.67598C41.237 6.48598 40.845 5.93398 39.988 5.93398C39.161 5.93398 38.696 6.57398 38.696 7.73398V11.713H36.896V4.53998ZM50.616 7.39998H54.231C54.115 6.45698 53.389 5.81798 52.417 5.81798C51.444 5.81798 50.717 6.45698 50.617 7.40098L50.616 7.39998ZM48.801 8.12698C48.801 5.90498 50.297 4.36698 52.431 4.36698C54.565 4.36698 56.046 5.89098 56.046 8.08298C56.046 8.25698 56.017 8.53298 55.988 8.72198H50.616C50.746 9.69498 51.574 10.348 52.634 10.348C53.418 10.348 54.101 10.058 54.551 9.53498L55.64 10.595C54.986 11.437 53.854 11.887 52.547 11.887C50.34 11.887 48.801 10.333 48.801 8.12698ZM58.906 7.39998H62.522C62.406 6.45698 61.68 5.81798 60.707 5.81798C59.734 5.81798 59.008 6.45698 58.907 7.40098L58.906 7.39998ZM57.092 8.12698C57.092 5.90498 58.587 4.36698 60.722 4.36698C62.856 4.36698 64.337 5.89098 64.337 8.08298C64.337 8.25698 64.307 8.53298 64.279 8.72198H58.906C59.037 9.69498 59.865 10.348 60.925 10.348C61.708 10.348 62.391 10.058 62.841 9.53498L63.93 10.595C63.277 11.437 62.144 11.887 60.837 11.887C58.631 11.887 57.092 10.333 57.092 8.12698ZM65.832 4.53998H67.531V5.51298H67.56C67.894 4.80198 68.62 4.36598 69.462 4.36598C71.262 4.36598 72.192 5.39698 72.192 7.37198V11.712H70.39V7.71998C70.39 6.49998 69.955 5.93398 69.04 5.93398C68.125 5.93398 67.631 6.57398 67.631 7.76398V11.713H65.831L65.832 4.53998ZM74.5 9.14298V6.03598H73.266V4.53998H73.919C74.514 4.53998 74.747 4.30798 74.747 3.71298V2.59498H76.286V4.53998H78.028V6.03598H76.286V9.14298C76.286 9.83998 76.648 10.232 77.316 10.232C77.52 10.232 77.766 10.202 77.912 10.159V11.669C77.602 11.7372 77.2855 11.7714 76.968 11.771C75.342 11.771 74.5 10.871 74.5 9.14298ZM80.728 7.39998H84.344C84.227 6.45698 83.501 5.81798 82.529 5.81798C81.556 5.81798 80.83 6.45698 80.729 7.40098L80.728 7.39998ZM78.913 8.12698C78.913 5.90498 80.409 4.36698 82.543 4.36698C84.678 4.36698 86.158 5.89098 86.158 8.08298C86.158 8.25698 86.129 8.53298 86.1 8.72198H80.728C80.859 9.69498 81.687 10.348 82.747 10.348C83.531 10.348 84.213 10.058 84.663 9.53498L85.752 10.595C85.099 11.437 83.966 11.887 82.659 11.887C80.452 11.887 78.913 10.333 78.913 8.12698Z",
      fill: "#EC0000"
    }), jsx("path", {
      className: "ams-logo__emblem",
      d: "M0 38.3642L3.6 34.9356L0 31.507L2.4 29.2213L6 32.6499L9.6 29.2213L12 31.507L8.4 34.9356L12 38.3642L9.6 40.6499L6 37.2213L2.4 40.6499L0 38.3642Z",
      fill: "#EC0000"
    }), jsx("path", {
      className: "ams-logo__emblem",
      d: "M0 9.79276L3.6 6.36419L0 2.93562L2.4 0.649902L6 4.07847L9.6 0.649902L12 2.93562L8.4 6.36419L12 9.79276L9.6 12.0785L6 8.6499L2.4 12.0785L0 9.79276Z",
      fill: "#EC0000"
    }), jsx("path", {
      className: "ams-logo__emblem",
      d: "M0 24.0785L3.6 20.6499L0 17.2213L2.4 14.9356L6 18.3642L9.6 14.9356L12 17.2213L8.4 20.6499L12 24.0785L9.6 26.3642L6 22.9356L2.4 26.3642L0 24.0785Z",
      fill: "#EC0000"
    })]
  }));
});
LogoMuseumWeesp.displayName = 'LogoMuseumWeesp';

function ownKeys$E(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$E(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$E(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$E(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoStadsarchief = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$E(_objectSpread$E({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 98 40",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__emblem",
      fill: "#EC0000",
      d: "m0 37.648 3.527-3.527L0 30.593l2.352-2.351 3.527 3.527 3.528-3.527 2.351 2.351-3.527 3.528 3.527 3.527L9.407 40l-3.528-3.527L2.352 40 0 37.648ZM0 9.407l3.527-3.528L0 2.352 2.352 0l3.527 3.527L9.407 0l2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.526 3.527L0 9.407ZM0 23.54l3.527-3.528L0 16.485l2.352-2.352 3.527 3.528 3.528-3.528 2.351 2.352-3.527 3.527 3.527 3.528-2.351 2.351-3.528-3.527-3.527 3.527L0 23.54Z"
    }), jsx("path", {
      className: "ams-logo__text-primary",
      fill: "#EC0000",
      d: "M20.937 15.334h2.17l3.76 9.4h-2.011l-.78-2.068h-4.18l-.782 2.068h-1.937l3.76-9.4Zm-.477 5.77h3.037l-1.504-3.962h-.03l-1.503 3.963ZM28.052 17.59h1.678v.969h.03c.36-.737.997-1.143 1.822-1.143.925 0 1.62.405 2.054 1.215.434-.781 1.228-1.215 2.24-1.215 1.766 0 2.618.983 2.618 2.994v4.324h-1.793v-4.02c0-1.17-.404-1.735-1.244-1.735s-1.287.622-1.287 1.793v3.962h-1.794v-4.02c0-1.186-.39-1.735-1.243-1.735-.854 0-1.288.636-1.288 1.793v3.962h-1.793V17.59ZM41.791 21.74c-.969-.288-1.91-.81-1.91-2.111 0-1.302 1.1-2.213 2.734-2.213 1.1 0 1.952.39 2.43 1.114l-1.027 1.026c-.362-.448-.853-.694-1.388-.694-.608 0-.983.276-.983.71 0 .578.608.679 1.49.954.968.29 1.909.81 1.909 2.112 0 1.301-1.157 2.27-2.863 2.27-1.114 0-2.01-.39-2.532-1.127l1.027-1.027c.39.464.911.71 1.49.71.693 0 1.113-.29 1.113-.767 0-.579-.608-.68-1.49-.955v-.001ZM46.94 22.175V19.08h-1.23v-1.49h.652c.592 0 .824-.232.824-.824v-1.114h1.533v1.938h1.736v1.49h-1.736v3.095c0 .693.361 1.085 1.027 1.085.202 0 .448-.03.592-.073v1.504a4.362 4.362 0 0 1-.94.101c-1.62 0-2.46-.897-2.46-2.617h.002ZM51.337 21.162c0-2.213 1.49-3.746 3.615-3.746 2.126 0 3.602 1.52 3.602 3.703 0 .174-.03.448-.058.636h-5.351c.13.969.955 1.62 2.01 1.62.781 0 1.46-.29 1.91-.81l1.084 1.056c-.651.838-1.779 1.287-3.08 1.287-2.2 0-3.731-1.547-3.731-3.746h-.002Zm1.808-.723h3.601c-.115-.94-.838-1.577-1.808-1.577-.97 0-1.692.637-1.793 1.577ZM60.042 17.59h1.692v1.186h.03c.346-.81.983-1.244 1.822-1.244.218 0 .521.03.636.072v1.706a3.906 3.906 0 0 0-.882-.1c-.91 0-1.504.693-1.504 1.764v3.76h-1.793V17.59ZM64.729 21.192c0-2.213 1.46-3.775 3.529-3.775.954 0 1.735.42 2.227 1.187h.029V14.25h1.793v10.486h-1.692v-1.114h-.03c-.462.81-1.33 1.288-2.327 1.288-2.069 0-3.529-1.548-3.529-3.717v-.002Zm5.785-.03c0-1.287-.796-2.14-1.967-2.14s-1.981.868-1.981 2.14c0 1.273.796 2.14 1.98 2.14 1.186 0 1.968-.853 1.968-2.14ZM73.68 22.695c0-1.374 1.143-2.198 3.052-2.212l1.388-.015v-.231c0-.838-.535-1.272-1.504-1.272-.636 0-1.2.245-1.59.693l-1.084-1.056c.622-.766 1.62-1.186 2.849-1.186 1.908 0 3.124 1.042 3.124 2.704v2.835c0 .26.115.377.404.377h.13v1.402h-.838c-.68 0-1.143-.318-1.287-.838h-.03c-.448.651-1.186 1.012-2.082 1.012-1.504 0-2.53-.897-2.53-2.213h-.002Zm4.44-.622v-.404l-1.185.014c-.94.014-1.446.347-1.446.969 0 .55.434.94 1.042.94.94 0 1.59-.621 1.59-1.518ZM81.851 17.59h1.678v.969h.03c.362-.737.998-1.143 1.822-1.143.925 0 1.62.405 2.054 1.215.434-.781 1.229-1.215 2.241-1.215 1.765 0 2.618.983 2.618 2.994v4.324H90.5v-4.02c0-1.17-.405-1.735-1.245-1.735-.84 0-1.287.622-1.287 1.793v3.962h-1.793v-4.02c0-1.186-.39-1.735-1.244-1.735s-1.288.636-1.288 1.793v3.962h-1.793V17.59ZM17.575 5.857c0-2.85 1.996-4.86 4.817-4.86 1.432 0 2.632.463 3.485 1.389l-1.244 1.186c-.535-.593-1.302-.94-2.213-.94-1.706 0-2.893 1.33-2.893 3.225 0 1.894 1.157 3.283 2.821 3.283 1.52 0 2.574-.868 2.574-2.126v-.246h-2.27V5.191h4.18v1.46c0 2.402-1.867 4.094-4.542 4.094s-4.715-2.025-4.715-4.888ZM28.047 7c0-2.213 1.49-3.747 3.616-3.747 2.126 0 3.601 1.52 3.601 3.703 0 .174-.03.448-.057.636h-5.351c.13.97.954 1.62 2.01 1.62.78 0 1.46-.289 1.91-.81l1.085 1.056c-.651.839-1.779 1.288-3.08 1.288-2.2 0-3.732-1.548-3.732-3.746h-.002Zm1.809-.724h3.601c-.115-.94-.838-1.576-1.808-1.576-.97 0-1.692.636-1.793 1.576ZM36.753 3.426h1.678v.97h.03c.361-.738.997-1.144 1.822-1.144.925 0 1.62.405 2.054 1.215.434-.78 1.23-1.215 2.24-1.215 1.766 0 2.618.983 2.618 2.994v4.324h-1.793V6.55c0-1.17-.404-1.735-1.244-1.735s-1.287.622-1.287 1.793v3.962h-1.794V6.55c0-1.186-.39-1.735-1.243-1.735-.854 0-1.288.636-1.288 1.793v3.962h-1.793V3.426ZM48.612 7c0-2.213 1.49-3.747 3.616-3.747 2.126 0 3.601 1.52 3.601 3.703 0 .174-.03.448-.057.636H50.42c.131.97.955 1.62 2.011 1.62.78 0 1.46-.289 1.91-.81l1.084 1.056c-.652.839-1.78 1.288-3.08 1.288-2.2 0-3.732-1.548-3.732-3.746Zm1.808-.724h3.602c-.115-.94-.838-1.576-1.807-1.576s-1.692.636-1.793 1.576h-.002ZM56.87 7c0-2.213 1.49-3.747 3.616-3.747 2.125 0 3.601 1.52 3.601 3.703 0 .174-.03.448-.058.636h-5.35c.13.97.954 1.62 2.01 1.62.78 0 1.46-.289 1.91-.81l1.084 1.056c-.652.839-1.78 1.288-3.081 1.288-2.199 0-3.731-1.548-3.731-3.746h-.001Zm1.808-.724h3.602c-.115-.94-.839-1.576-1.809-1.576s-1.692.636-1.793 1.576ZM65.577 3.426h1.692v.97h.03c.332-.71 1.055-1.144 1.893-1.144 1.794 0 2.719 1.027 2.719 2.994v4.324h-1.793V6.594c0-1.215-.434-1.78-1.345-1.78-.912 0-1.403.637-1.403 1.823v3.935h-1.793V3.426ZM74.21 8.012V4.917h-1.23v-1.49h.652c.593 0 .825-.231.825-.824V1.49h1.533v1.937h1.735v1.49H75.99v3.095c0 .694.36 1.085 1.026 1.085.203 0 .448-.03.593-.073v1.504a4.356 4.356 0 0 1-.94.101c-1.621 0-2.46-.897-2.46-2.617l.002.001ZM78.607 7c0-2.213 1.49-3.747 3.616-3.747 2.126 0 3.601 1.52 3.601 3.703 0 .174-.03.448-.057.636h-5.352c.131.97.955 1.62 2.011 1.62.78 0 1.46-.289 1.91-.81l1.084 1.056c-.652.839-1.78 1.288-3.08 1.288-2.2 0-3.732-1.548-3.732-3.746Zm1.808-.724h3.602c-.115-.94-.838-1.576-1.808-1.576-.97 0-1.692.636-1.794 1.576Z"
    }), jsx("path", {
      className: "ams-logo__text-secondary",
      fill: "#000",
      d: "m17.335 37.353 1.302-1.084a2.07 2.07 0 0 0 1.779.983c.868 0 1.46-.463 1.46-1.143 0-.766-.636-1.042-1.866-1.49-1.143-.42-2.343-1.012-2.343-2.747 0-1.62 1.316-2.734 3.24-2.734 1.3 0 2.327.52 2.906 1.446L22.51 31.67c-.39-.578-.954-.897-1.62-.897-.78 0-1.302.42-1.302 1.042 0 .767.637 1.042 1.866 1.49 1.143.42 2.344 1.012 2.344 2.748 0 1.735-1.375 2.834-3.4 2.834-1.374 0-2.458-.549-3.066-1.533h.002ZM25.738 36.153v-3.095h-1.23v-1.49h.65c.593 0 .824-.231.824-.824v-1.113h1.534v1.937h1.735v1.49h-1.735v3.095c0 .693.362 1.085 1.026 1.085.202 0 .448-.03.593-.073v1.504a4.37 4.37 0 0 1-.941.101c-1.62 0-2.459-.897-2.459-2.617h.003ZM30.134 36.674c0-1.375 1.143-2.2 3.051-2.213l1.389-.014v-.232c0-.838-.535-1.272-1.504-1.272-.636 0-1.2.245-1.59.694l-1.085-1.056c.622-.767 1.62-1.187 2.85-1.187 1.909 0 3.123 1.042 3.123 2.705v2.835c0 .26.115.376.405.376h.13v1.402h-.838c-.68 0-1.143-.318-1.288-.838h-.029c-.448.651-1.186 1.012-2.082 1.012-1.504 0-2.53-.897-2.53-2.212h-.002Zm4.44-.622v-.405l-1.186.014c-.941.014-1.447.347-1.447.97 0 .548.434.94 1.042.94.94 0 1.591-.622 1.591-1.52ZM37.813 35.17c0-2.213 1.46-3.775 3.529-3.775.955 0 1.735.42 2.227 1.187h.03v-4.353h1.792v10.486H43.7V37.6h-.029c-.463.81-1.331 1.288-2.328 1.288-2.068 0-3.529-1.547-3.529-3.717v-.002Zm5.785-.03c0-1.287-.796-2.14-1.967-2.14-1.17 0-1.981.868-1.981 2.14 0 1.273.796 2.14 1.981 2.14s1.967-.853 1.967-2.14ZM48.763 35.719c-.97-.29-1.91-.81-1.91-2.112 0-1.301 1.1-2.213 2.734-2.213 1.1 0 1.952.39 2.429 1.114l-1.026 1.026c-.363-.448-.854-.694-1.39-.694-.607 0-.982.276-.982.71 0 .578.608.68 1.49.954.969.29 1.91.81 1.91 2.112s-1.158 2.27-2.864 2.27c-1.113 0-2.01-.39-2.532-1.127l1.027-1.027c.39.464.911.71 1.49.71.694 0 1.113-.29 1.113-.767 0-.579-.608-.68-1.49-.955v-.001ZM52.97 36.674c0-1.375 1.144-2.2 3.052-2.213l1.389-.014v-.232c0-.838-.535-1.272-1.504-1.272-.636 0-1.2.245-1.591.694l-1.084-1.056c.622-.767 1.62-1.187 2.849-1.187 1.91 0 3.124 1.042 3.124 2.705v2.835c0 .26.115.376.404.376h.13v1.402h-.837c-.68 0-1.143-.318-1.288-.838h-.03c-.447.651-1.186 1.012-2.082 1.012-1.504 0-2.53-.897-2.53-2.212h-.001Zm4.44-.622v-.405l-1.186.014c-.94.014-1.446.347-1.446.97 0 .548.434.94 1.042.94.94 0 1.59-.622 1.59-1.52ZM61.142 31.568h1.691v1.187h.03c.347-.81.983-1.244 1.822-1.244.218 0 .521.029.636.073v1.706a3.907 3.907 0 0 0-.881-.102c-.912 0-1.504.694-1.504 1.765v3.76h-1.793V31.57h-.002ZM65.827 35.14c0-2.227 1.49-3.746 3.689-3.746 1.085 0 2.038.405 2.646 1.128l-1.2 1.127c-.362-.404-.868-.622-1.446-.622-1.1 0-1.851.868-1.851 2.112 0 1.244.752 2.112 1.85 2.112.58 0 1.086-.218 1.476-.636l1.187 1.127c-.636.738-1.577 1.143-2.647 1.143-2.199 0-3.703-1.519-3.703-3.746v.001ZM73.492 28.228h1.793v4.15h.03c.42-.636 1.041-.983 1.793-.983 1.793 0 2.718 1.027 2.718 2.994v4.325h-1.793v-3.978c0-1.215-.434-1.78-1.345-1.78s-1.403.637-1.403 1.823v3.935h-1.793V28.228ZM82.56 30.657c-.622 0-1.07-.434-1.07-1.042 0-.608.448-1.042 1.07-1.042.622 0 1.07.434 1.07 1.042 0 .608-.448 1.042-1.07 1.042Zm-.897.911h1.793v7.144h-1.793v-7.144ZM84.947 35.14c0-2.212 1.49-3.746 3.616-3.746 2.126 0 3.601 1.52 3.601 3.703 0 .174-.03.448-.058.636h-5.35c.13.969.954 1.62 2.01 1.62.78 0 1.46-.289 1.91-.81L91.76 37.6c-.652.838-1.78 1.288-3.081 1.288-2.199 0-3.73-1.548-3.73-3.747h-.002Zm1.808-.723h3.602c-.115-.94-.838-1.577-1.808-1.577-.97 0-1.693.637-1.794 1.577ZM92.886 33.058v-1.49h1.215v-.925c0-1.634.969-2.59 2.632-2.59.203 0 .506.03.666.058v1.548a2.82 2.82 0 0 0-.622-.072c-.565 0-.882.376-.882 1.056v.925h1.736v1.49h-1.736v5.654h-1.793v-5.654h-1.216Z"
    })]
  }));
});
LogoStadsarchief.displayName = 'LogoStadsarchief';

function ownKeys$D(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$D(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$D(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$D(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoStadsbankVanLening = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$D(_objectSpread$D({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 159 42",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__emblem",
      fill: "#EC0000",
      d: "m0 37.65 3.53-3.53L0 30.59l2.35-2.35 3.53 3.53 3.53-3.53 2.35 2.35-3.53 3.53 3.53 3.53L9.41 40l-3.53-3.53L2.35 40 0 37.65ZM0 9.41l3.53-3.53L0 2.35 2.35 0l3.53 3.53L9.41 0l2.35 2.35-3.53 3.53 3.53 3.53-2.35 2.35-3.53-3.53-3.53 3.53L0 9.41ZM0 23.54l3.53-3.53L0 16.48l2.35-2.35 3.53 3.53 3.53-3.53 2.35 2.35-3.53 3.53 3.53 3.53-2.35 2.35-3.53-3.53-3.53 3.53L0 23.54Z"
    }), jsx("path", {
      className: "ams-logo__text-secondary",
      fill: "#000",
      d: "m17.34 37.35 1.3-1.08c.4.64 1.06.98 1.78.98.87 0 1.46-.46 1.46-1.14 0-.77-.64-1.04-1.87-1.49-1.14-.42-2.34-1.01-2.34-2.75 0-1.62 1.32-2.73 3.24-2.73 1.3 0 2.33.52 2.91 1.45l-1.3 1.08c-.39-.58-.95-.9-1.62-.9-.78 0-1.3.42-1.3 1.04 0 .77.64 1.04 1.87 1.49 1.14.42 2.34 1.01 2.34 2.75s-1.37 2.83-3.4 2.83c-1.37 0-2.46-.55-3.07-1.53ZM25.74 36.15v-3.1h-1.23v-1.49h.65c.59 0 .82-.23.82-.82v-1.11h1.53v1.94h1.74v1.49h-1.74v3.1c0 .69.36 1.08 1.03 1.08.2 0 .45-.03.59-.07v1.5c-.22.06-.62.1-.94.1-1.62 0-2.46-.9-2.46-2.62h.01ZM30.13 36.67c0-1.37 1.14-2.2 3.05-2.21h1.39v-.25c0-.84-.54-1.27-1.5-1.27-.64 0-1.2.25-1.59.69l-1.08-1.06c.62-.77 1.62-1.19 2.85-1.19 1.91 0 3.12 1.04 3.12 2.7v2.83c0 .26.12.38.4.38h.13v1.4h-.84c-.68 0-1.14-.32-1.29-.84h-.03c-.45.65-1.19 1.01-2.08 1.01-1.5 0-2.53-.9-2.53-2.21v.02Zm4.44-.62v-.4h-1.19c-.94.03-1.45.36-1.45.98 0 .55.43.94 1.04.94.94 0 1.59-.62 1.59-1.52h.01ZM37.81 35.17c0-2.21 1.46-3.77 3.53-3.77.95 0 1.74.42 2.23 1.19h.03v-4.35h1.79v10.49H43.7v-1.11h-.03c-.46.81-1.33 1.29-2.33 1.29-2.07 0-3.53-1.55-3.53-3.72v-.02Zm5.79-.03c0-1.29-.8-2.14-1.97-2.14s-1.98.87-1.98 2.14.8 2.14 1.98 2.14 1.97-.85 1.97-2.14ZM48.76 35.72c-.97-.29-1.91-.81-1.91-2.11 0-1.3 1.1-2.21 2.73-2.21 1.1 0 1.95.39 2.43 1.11l-1.03 1.03c-.36-.45-.85-.69-1.39-.69-.61 0-.98.27-.98.71 0 .58.61.68 1.49.95.97.29 1.91.81 1.91 2.11 0 1.3-1.16 2.27-2.86 2.27-1.11 0-2.01-.39-2.53-1.13l1.03-1.03c.39.46.91.71 1.49.71.69 0 1.11-.29 1.11-.77 0-.58-.61-.68-1.49-.95ZM55.18 37.6h-.03v1.11h-1.71V28.22h1.79v4.35h.03c.52-.77 1.3-1.19 2.26-1.19 2.04 0 3.5 1.56 3.5 3.75s-1.46 3.75-3.5 3.75c-1 0-1.82-.46-2.34-1.29v.01Zm4.01-2.46c0-1.29-.8-2.14-1.97-2.14s-1.98.87-1.98 2.14.81 2.14 1.98 2.14 1.97-.85 1.97-2.14ZM61.95 36.67c0-1.37 1.14-2.2 3.05-2.21h1.39v-.25c0-.84-.54-1.27-1.5-1.27-.64 0-1.2.25-1.59.69l-1.08-1.06c.62-.77 1.62-1.19 2.85-1.19 1.91 0 3.12 1.04 3.12 2.7v2.83c0 .26.12.38.4.38h.13v1.4h-.84c-.68 0-1.14-.32-1.29-.84h-.03c-.45.65-1.19 1.01-2.08 1.01-1.5 0-2.53-.9-2.53-2.21v.02Zm4.44-.62v-.4H65.2c-.94.03-1.45.36-1.45.98 0 .55.43.94 1.04.94.94 0 1.59-.62 1.59-1.52h.01ZM70.12 31.57h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.35-1.78-.92 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14.01ZM80.09 38.71H78.3V28.22h1.79v6.57h.03l2.66-3.23h2.17l-2.76 3.17 2.89 3.98h-2.17l-1.98-2.81-.84.97v1.84ZM88.97 31.57h1.95l1.66 5.15h.03l1.71-5.15h1.91l-2.69 7.14h-1.92l-2.65-7.14ZM96.6 36.67c0-1.37 1.14-2.2 3.05-2.21h1.39v-.25c0-.84-.54-1.27-1.5-1.27-.64 0-1.2.25-1.59.69l-1.08-1.06c.62-.77 1.62-1.19 2.85-1.19 1.91 0 3.12 1.04 3.12 2.7v2.83c0 .26.12.38.4.38h.13v1.4h-.84c-.68 0-1.14-.32-1.29-.84h-.03c-.45.65-1.19 1.01-2.08 1.01-1.5 0-2.53-.9-2.53-2.21v.02Zm4.44-.62v-.4h-1.19c-.94.03-1.45.36-1.45.98 0 .55.43.94 1.04.94.94 0 1.59-.62 1.59-1.52h.01ZM104.77 31.57h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.34-1.78-.91 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14l-.01.01ZM116.65 29.31h1.91v7.78h3.49v1.62h-5.39v-9.4h-.01ZM122.87 35.14c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM131.57 31.57h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.35-1.78-.92 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14.01ZM140.64 30.66c-.62 0-1.07-.43-1.07-1.04 0-.61.45-1.04 1.07-1.04.62 0 1.07.43 1.07 1.04 0 .61-.45 1.04-1.07 1.04Zm-.9.91h1.79v7.14h-1.79v-7.14ZM143.47 31.57h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.35-1.78-.92 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14.01ZM151.54 40.42l1.16-1.13c.39.45 1.1.72 1.87.72 1.3 0 2.02-.59 2.02-1.63v-1.1h-.03c-.4.75-1.16 1.17-2.1 1.17-2.01 0-3.34-1.42-3.34-3.53s1.37-3.51 3.34-3.51c1.01 0 1.81.45 2.2 1.21h.03v-1.06h1.69v6.88c0 1.91-1.46 3.11-3.79 3.11-1.33 0-2.43-.42-3.05-1.14v.01Zm5.06-5.5c0-1.19-.74-2.01-1.82-2.01s-1.82.82-1.82 2.01.75 2 1.82 2 1.82-.82 1.82-2Z"
    }), jsx("path", {
      className: "ams-logo__text-primary",
      fill: "#EC0000",
      d: "M20.94 15.33h2.17l3.76 9.4h-2.01l-.78-2.07H19.9l-.78 2.07h-1.94l3.76-9.4Zm-.48 5.77h3.04L22 17.14h-.03l-1.5 3.96h-.01ZM28.05 17.59h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H36.7v-4.02c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79V20.7c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79v-7.14l-.01.02ZM41.79 21.74c-.97-.29-1.91-.81-1.91-2.11 0-1.3 1.1-2.21 2.73-2.21 1.1 0 1.95.39 2.43 1.11l-1.03 1.03c-.36-.45-.85-.69-1.39-.69-.61 0-.98.27-.98.71 0 .58.61.68 1.49.95.97.29 1.91.81 1.91 2.11 0 1.3-1.16 2.27-2.86 2.27-1.11 0-2.01-.39-2.53-1.13l1.03-1.03c.39.46.91.71 1.49.71.69 0 1.11-.29 1.11-.77 0-.58-.61-.68-1.49-.95ZM46.94 22.17v-3.09h-1.23v-1.49h.65c.59 0 .82-.23.82-.82v-1.11h1.53v1.94h1.74v1.49h-1.74v3.09c0 .69.36 1.08 1.03 1.08.2 0 .45-.03.59-.07v1.5c-.22.06-.62.1-.94.1-1.62 0-2.46-.9-2.46-2.62h.01ZM51.34 21.16c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM60.04 17.59h1.69v1.19h.03c.35-.81.98-1.24 1.82-1.24.22 0 .52.03.64.07v1.71c-.2-.06-.59-.1-.88-.1-.91 0-1.5.69-1.5 1.76v3.76h-1.79V17.6l-.01-.01ZM64.73 21.19c0-2.21 1.46-3.77 3.53-3.77.95 0 1.74.42 2.23 1.19h.03v-4.35h1.79v10.49h-1.69v-1.11h-.03c-.46.81-1.33 1.29-2.33 1.29-2.07 0-3.53-1.55-3.53-3.72v-.02Zm5.79-.03c0-1.29-.8-2.14-1.97-2.14s-1.98.87-1.98 2.14.8 2.14 1.98 2.14 1.97-.85 1.97-2.14ZM73.68 22.7c0-1.37 1.14-2.2 3.05-2.21h1.39v-.25c0-.84-.54-1.27-1.5-1.27-.64 0-1.2.25-1.59.69l-1.08-1.06c.62-.77 1.62-1.19 2.85-1.19 1.91 0 3.12 1.04 3.12 2.7v2.83c0 .26.12.38.4.38h.13v1.4h-.84c-.68 0-1.14-.32-1.29-.84h-.03c-.45.65-1.19 1.01-2.08 1.01-1.5 0-2.53-.9-2.53-2.21v.02Zm4.44-.62v-.4h-1.19c-.94.03-1.45.36-1.45.98 0 .55.43.94 1.04.94.94 0 1.59-.62 1.59-1.52h.01ZM81.85 17.59h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H90.5v-4.02c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79V20.7c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79v-7.14l-.01.02ZM17.58 5.86c0-2.85 2-4.86 4.82-4.86 1.43 0 2.63.46 3.49 1.39l-1.24 1.19c-.54-.59-1.3-.94-2.21-.94-1.71 0-2.89 1.33-2.89 3.23s1.16 3.28 2.82 3.28c1.52 0 2.57-.87 2.57-2.13v-.25h-2.27V5.19h4.18v1.46c0 2.4-1.87 4.09-4.54 4.09S17.6 8.72 17.6 5.85l-.02.01ZM28.05 7c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM36.75 3.43h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H45.4V6.55c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79V6.55c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79V3.43h-.01ZM48.61 7c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM56.87 7c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM65.58 3.43h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79V6.59c0-1.21-.43-1.78-1.34-1.78-.91 0-1.4.64-1.4 1.82v3.93h-1.79V3.43h-.01ZM74.21 8.01v-3.1h-1.23V3.42h.65c.59 0 .82-.23.82-.82V1.49h1.53v1.94h1.74v1.49h-1.74v3.1c0 .69.36 1.08 1.03 1.08.2 0 .45-.03.59-.07v1.5c-.22.06-.62.1-.94.1-1.62 0-2.46-.9-2.46-2.62h.01ZM78.61 7c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58Z"
    })]
  }));
});
LogoStadsbankVanLening.displayName = 'LogoStadsbankVanLening';

function ownKeys$C(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$C(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$C(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$C(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var LogoVgaVerzekeringen = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("svg", _objectSpread$C(_objectSpread$C({
    className: "ams-logo",
    "aria-hidden": "true",
    focusable: "false",
    ref: ref,
    viewBox: "0 0 145 43",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), {}, {
    children: [jsx("path", {
      className: "ams-logo__emblem",
      fill: "#EC0000",
      d: "m0 38.2 3.53-3.53L0 31.14l2.35-2.35 3.53 3.53 3.53-3.53 2.35 2.35-3.53 3.53 3.53 3.53-2.35 2.35-3.53-3.53-3.53 3.53L0 38.2ZM0 9.96l3.53-3.53L0 2.9 2.35.55l3.53 3.53L9.41.55l2.35 2.35-3.53 3.53 3.53 3.53-2.35 2.35-3.53-3.53-3.53 3.53L0 9.96ZM0 24.09l3.53-3.53L0 17.03l2.35-2.35 3.53 3.53 3.53-3.53 2.35 2.35-3.53 3.53 3.53 3.53-2.35 2.35-3.53-3.53-3.53 3.53L0 24.09Z"
    }), jsx("path", {
      className: "ams-logo__text-secondary",
      fill: "#000",
      d: "M17.13 29.86h2.07l2.39 7.22h.03l2.42-7.22h1.98l-3.36 9.4h-2.17l-3.36-9.4ZM26.5 34.55c0-2.85 2-4.86 4.82-4.86 1.43 0 2.63.46 3.49 1.39l-1.24 1.19c-.54-.59-1.3-.94-2.21-.94-1.71 0-2.89 1.33-2.89 3.23s1.16 3.28 2.82 3.28c1.52 0 2.57-.87 2.57-2.13v-.25h-2.27v-1.58h4.18v1.46c0 2.4-1.87 4.09-4.54 4.09s-4.71-2.02-4.71-4.89l-.02.01ZM40.43 29.86h2.17l3.76 9.4h-2.01l-.78-2.07h-4.18l-.78 2.07h-1.94l3.76-9.4Zm-.48 5.77h3.04l-1.5-3.96h-.03l-1.5 3.96h-.01ZM50.38 29.86h2.07l2.39 7.22h.03l2.42-7.22h1.98l-3.36 9.4h-2.17l-3.36-9.4ZM59.42 35.69c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM68.13 32.12h1.69v1.19h.03c.35-.81.98-1.24 1.82-1.24.22 0 .52.03.64.07v1.71c-.2-.06-.59-.1-.88-.1-.91 0-1.5.69-1.5 1.76v3.76h-1.79v-7.14l-.01-.01ZM73.01 38.06l3.4-4.45h-3.3v-1.49h5.73v1.2l-3.4 4.45h3.5v1.49h-5.93v-1.2ZM79.88 35.69c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM90.38 39.26h-1.79V28.77h1.79v6.57h.03l2.66-3.23h2.17l-2.76 3.17 2.89 3.98H93.2l-1.98-2.81-.84.97v1.84ZM95.68 35.69c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM104.38 32.12h1.69v1.19h.03c.35-.81.98-1.24 1.82-1.24.22 0 .52.03.64.07v1.71c-.2-.06-.59-.1-.88-.1-.91 0-1.5.69-1.5 1.76v3.76h-1.79v-7.14l-.01-.01ZM110.65 31.21c-.62 0-1.07-.43-1.07-1.04 0-.61.45-1.04 1.07-1.04.62 0 1.07.43 1.07 1.04 0 .61-.45 1.04-1.07 1.04Zm-.9.91h1.79v7.14h-1.79v-7.14ZM113.48 32.12h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.35-1.78-.92 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14.01ZM121.55 40.97l1.16-1.13c.39.45 1.1.72 1.87.72 1.3 0 2.02-.59 2.02-1.63v-1.1h-.03c-.4.75-1.16 1.17-2.1 1.17-2.01 0-3.34-1.42-3.34-3.53s1.37-3.51 3.34-3.51c1.01 0 1.81.45 2.2 1.21h.03v-1.06h1.69v6.88c0 1.91-1.46 3.11-3.79 3.11-1.33 0-2.43-.42-3.05-1.14v.01Zm5.06-5.5c0-1.19-.74-2.01-1.82-2.01s-1.82.82-1.82 2.01.75 2 1.82 2 1.82-.82 1.82-2ZM129.88 35.69c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM138.59 32.12h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79v-3.98c0-1.21-.43-1.78-1.35-1.78-.92 0-1.4.64-1.4 1.82v3.93h-1.79v-7.14.01Z"
    }), jsx("path", {
      className: "ams-logo__text-primary",
      fill: "#EC0000",
      d: "M20.94 15.88h2.17l3.76 9.4h-2.01l-.78-2.07H19.9l-.78 2.07h-1.94l3.76-9.4Zm-.48 5.77h3.04L22 17.69h-.03l-1.5 3.96h-.01ZM28.05 18.14h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H36.7v-4.02c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79v-4.02c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79v-7.14l-.01.02ZM41.79 22.29c-.97-.29-1.91-.81-1.91-2.11 0-1.3 1.1-2.21 2.73-2.21 1.1 0 1.95.39 2.43 1.11l-1.03 1.03c-.36-.45-.85-.69-1.39-.69-.61 0-.98.27-.98.71 0 .58.61.68 1.49.95.97.29 1.91.81 1.91 2.11 0 1.3-1.16 2.27-2.86 2.27-1.11 0-2.01-.39-2.53-1.13l1.03-1.03c.39.46.91.71 1.49.71.69 0 1.11-.29 1.11-.77 0-.58-.61-.68-1.49-.95ZM46.94 22.72v-3.09h-1.23v-1.49h.65c.59 0 .82-.23.82-.82v-1.11h1.53v1.94h1.74v1.49h-1.74v3.09c0 .69.36 1.08 1.03 1.08.2 0 .45-.03.59-.07v1.5c-.22.06-.62.1-.94.1-1.62 0-2.46-.9-2.46-2.62h.01ZM51.34 21.71c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM60.04 18.14h1.69v1.19h.03c.35-.81.98-1.24 1.82-1.24.22 0 .52.03.64.07v1.71c-.2-.06-.59-.1-.88-.1-.91 0-1.5.69-1.5 1.76v3.76h-1.79v-7.14l-.01-.01ZM64.73 21.74c0-2.21 1.46-3.77 3.53-3.77.95 0 1.74.42 2.23 1.19h.03v-4.35h1.79V25.3h-1.69v-1.11h-.03c-.46.81-1.33 1.29-2.33 1.29-2.07 0-3.53-1.55-3.53-3.72v-.02Zm5.79-.03c0-1.29-.8-2.14-1.97-2.14s-1.98.87-1.98 2.14.8 2.14 1.98 2.14 1.97-.85 1.97-2.14ZM73.68 23.25c0-1.37 1.14-2.2 3.05-2.21h1.39v-.25c0-.84-.54-1.27-1.5-1.27-.64 0-1.2.25-1.59.69l-1.08-1.06c.62-.77 1.62-1.19 2.85-1.19 1.91 0 3.12 1.04 3.12 2.7v2.83c0 .26.12.38.4.38h.13v1.4h-.84c-.68 0-1.14-.32-1.29-.84h-.03c-.45.65-1.19 1.01-2.08 1.01-1.5 0-2.53-.9-2.53-2.21v.02Zm4.44-.62v-.4h-1.19c-.94.03-1.45.36-1.45.98 0 .55.43.94 1.04.94.94 0 1.59-.62 1.59-1.52h.01ZM81.85 18.14h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H90.5v-4.02c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79v-4.02c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79v-7.14l-.01.02ZM17.58 6.41c0-2.85 2-4.86 4.82-4.86 1.43 0 2.63.46 3.49 1.39l-1.24 1.19c-.54-.59-1.3-.94-2.21-.94-1.71 0-2.89 1.33-2.89 3.23s1.16 3.28 2.82 3.28c1.52 0 2.57-.87 2.57-2.13v-.25h-2.27V5.74h4.18V7.2c0 2.4-1.87 4.09-4.54 4.09S17.6 9.27 17.6 6.4l-.02.01ZM28.05 7.55c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM36.75 3.98h1.68v.97h.03c.36-.74 1-1.14 1.82-1.14.93 0 1.62.4 2.05 1.21.43-.78 1.23-1.21 2.24-1.21 1.76 0 2.62.98 2.62 2.99v4.32H45.4V7.1c0-1.17-.4-1.74-1.24-1.74s-1.29.62-1.29 1.79v3.96h-1.79V7.1c0-1.19-.39-1.74-1.24-1.74s-1.29.64-1.29 1.79v3.96h-1.79V3.98h-.01ZM48.61 7.55c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM56.87 7.55c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58ZM65.58 3.98h1.69v.97h.03c.33-.71 1.06-1.14 1.89-1.14 1.79 0 2.72 1.03 2.72 2.99v4.32h-1.79V7.14c0-1.21-.43-1.78-1.34-1.78-.91 0-1.4.64-1.4 1.82v3.93h-1.79V3.98h-.01ZM74.21 8.56V5.47h-1.23V3.98h.65c.59 0 .82-.23.82-.82V2.04h1.53v1.94h1.74v1.49h-1.74v3.09c0 .69.36 1.08 1.03 1.08.2 0 .45-.03.59-.07v1.5c-.22.06-.62.1-.94.1-1.62 0-2.46-.9-2.46-2.62l.01.01ZM78.61 7.55c0-2.21 1.49-3.75 3.62-3.75s3.6 1.52 3.6 3.7c0 .17-.03.45-.06.64h-5.35c.13.97.95 1.62 2.01 1.62.78 0 1.46-.29 1.91-.81l1.08 1.06c-.65.84-1.78 1.29-3.08 1.29-2.2 0-3.73-1.55-3.73-3.75Zm1.81-.72h3.6c-.12-.94-.84-1.58-1.81-1.58-.97 0-1.69.64-1.79 1.58Z"
    })]
  }));
});
LogoVgaVerzekeringen.displayName = 'LogoVgaVerzekeringen';

var _excluded$B = ["brand", "className"];
function ownKeys$B(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$B(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$B(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$B(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var logoConfig = {
  amsterdam: LogoAmsterdam,
  'ggd-amsterdam': LogoGgdAmsterdam,
  'museum-weesp': LogoMuseumWeesp,
  stadsarchief: LogoStadsarchief,
  'stadsbank-van-lening': LogoStadsbankVanLening,
  'vga-verzekeringen': LogoVgaVerzekeringen
};
var Logo = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$brand = _ref.brand,
    brand = _ref$brand === void 0 ? 'amsterdam' : _ref$brand,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$B);
  var LogoComponent = logoConfig[brand];
  return jsx(LogoComponent, _objectSpread$B(_objectSpread$B({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-logo', className)
  }));
});
Logo.displayName = 'Logo';

var _excluded$A = ["appName", "className", "links", "logoBrand", "logoLink", "logoLinkTitle", "menu"];
function ownKeys$A(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$A(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$A(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$A(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Header = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var appName = _ref.appName,
    className = _ref.className,
    links = _ref.links,
    _ref$logoBrand = _ref.logoBrand,
    logoBrand = _ref$logoBrand === void 0 ? 'amsterdam' : _ref$logoBrand,
    _ref$logoLink = _ref.logoLink,
    logoLink = _ref$logoLink === void 0 ? '/' : _ref$logoLink,
    _ref$logoLinkTitle = _ref.logoLinkTitle,
    logoLinkTitle = _ref$logoLinkTitle === void 0 ? 'Ga naar de homepage' : _ref$logoLinkTitle,
    menu = _ref.menu,
    restProps = _objectWithoutProperties(_ref, _excluded$A);
  return jsx(Fragment, {
    children: jsxs("header", _objectSpread$A(_objectSpread$A({}, restProps), {}, {
      ref: ref,
      className: clsx('ams-header', className),
      children: [jsxs("a", {
        href: logoLink,
        className: "ams-header__logo-link",
        children: [jsx("span", {
          className: "ams-visually-hidden",
          children: logoLinkTitle
        }), jsx(Logo, {
          brand: logoBrand
        })]
      }), links && jsx("div", {
        className: "ams-header__links",
        children: links
      }), menu && jsx("div", {
        className: "ams-header__menu",
        children: menu
      }), appName && jsx("div", {
        className: "ams-header__app-name",
        children: jsx(Heading, {
          level: 1,
          size: "level-3",
          className: "ams-header__app-name-heading",
          children: appName
        })
      })]
    }))
  });
});
Header.displayName = 'Header';

var _excluded$z = ["children", "className"];
function ownKeys$z(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$z(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$z(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$z(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Mark = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$z);
  return jsx("mark", _objectSpread$z(_objectSpread$z({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-mark', className),
    children: children
  }));
});
Mark.displayName = 'Mark';

var _excluded$y = ["className", "dir", "invalid", "type"];
function ownKeys$y(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$y(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$y(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$y(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TextInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    dir = _ref.dir,
    invalid = _ref.invalid,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'text' : _ref$type,
    restProps = _objectWithoutProperties(_ref, _excluded$y);
  return jsx("input", _objectSpread$y(_objectSpread$y({}, restProps), {}, {
    "aria-invalid": invalid || undefined,
    className: clsx('ams-text-input', className),
    dir: dir !== null && dir !== void 0 ? dir : 'auto',
    ref: ref,
    type: type
  }));
});
TextInput.displayName = 'TextInput';

var _excluded$x = ["label", "className"];
function ownKeys$x(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$x(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$x(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$x(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// TODO: replace this with IconButton when that's done
// TODO: discuss if IconButton is the right component to replace this
var SearchFieldButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Zoeken' : _ref$label,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$x);
  return jsxs("button", _objectSpread$x(_objectSpread$x({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-search-field__button', className),
    children: [jsx("span", {
      className: "ams-visually-hidden",
      children: label
    }), jsx(Icon, {
      svg: SearchIcon,
      size: "level-5",
      square: true
    })]
  }));
});
SearchFieldButton.displayName = 'SearchField.Button';

var _excluded$w = ["className", "dir", "invalid", "label"];
function ownKeys$w(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$w(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$w(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$w(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SearchFieldInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    dir = _ref.dir,
    invalid = _ref.invalid,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Zoeken' : _ref$label,
    restProps = _objectWithoutProperties(_ref, _excluded$w);
  var id = useId();
  return jsxs(Fragment, {
    children: [jsx("label", {
      htmlFor: id,
      className: "ams-visually-hidden",
      children: label
    }), jsx("input", _objectSpread$w(_objectSpread$w({}, restProps), {}, {
      "aria-invalid": invalid || undefined,
      autoComplete: "off",
      className: clsx('ams-search-field__input', className),
      dir: dir !== null && dir !== void 0 ? dir : 'auto',
      enterKeyHint: "search",
      id: id,
      ref: ref,
      spellCheck: "false",
      type: "search"
    }))]
  });
});
SearchFieldInput.displayName = 'SearchField.Input';

var _excluded$v = ["children", "className"];
function ownKeys$v(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$v(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$v(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$v(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var SearchFieldRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$v);
  return jsx("form", _objectSpread$v(_objectSpread$v({
    role: "search"
  }, restProps), {}, {
    ref: ref,
    className: clsx('ams-search-field', className),
    children: children
  }));
});
SearchFieldRoot.displayName = 'SearchField';
var SearchField = Object.assign(SearchFieldRoot, {
  Button: SearchFieldButton,
  Input: SearchFieldInput
});

var _excluded$u = ["children", "className", "closeButtonLabel", "footer", "heading"];
function ownKeys$u(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$u(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$u(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$u(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var closeDialog = function closeDialog(event) {
  var _event$currentTarget$;
  return (_event$currentTarget$ = event.currentTarget.closest('dialog')) === null || _event$currentTarget$ === void 0 ? void 0 : _event$currentTarget$.close();
};
var openDialog = function openDialog(id) {
  var _document$querySelect;
  return (_document$querySelect = document.querySelector(id)) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.showModal();
};
var DialogRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$closeButtonLabel = _ref.closeButtonLabel,
    closeButtonLabel = _ref$closeButtonLabel === void 0 ? 'Sluiten' : _ref$closeButtonLabel,
    footer = _ref.footer,
    heading = _ref.heading,
    restProps = _objectWithoutProperties(_ref, _excluded$u);
  return jsxs("dialog", _objectSpread$u(_objectSpread$u({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-dialog', className),
    children: [jsxs("header", {
      className: "ams-dialog__header",
      children: [jsx(Heading, {
        size: "level-4",
        children: heading
      }), jsx(IconButton, {
        label: closeButtonLabel,
        onClick: closeDialog,
        size: "level-4",
        type: "button"
      })]
    }), jsx("div", {
      className: "ams-dialog__body",
      children: children
    }), footer && jsx("footer", {
      className: "ams-dialog__footer",
      children: footer
    })]
  }));
});
DialogRoot.displayName = 'Dialog';
var Dialog = Object.assign(DialogRoot, {
  close: closeDialog,
  open: openDialog
});

var _excluded$t = ["className", "maxVisiblePages", "nextLabel", "nextVisuallyHiddenLabel", "onPageChange", "page", "previousLabel", "previousVisuallyHiddenLabel", "totalPages", "visuallyHiddenLabel"];
function ownKeys$t(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$t(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$t(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$t(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * This returns an array of the range, including spacers
 *
 * @example
 * currentPage = 4, totalPages = 7, maxVisiblePages = 7
 * // returns [1, 2, 3, 4, 5, 6, 7]
 *
 * @example
 * currentPage = 5, totalPages = 100, maxVisiblePages = 7
 * // returns [1, 'firstSpacer', 4, 5, 6, 'lastSpacer', 100]
 *
 * @example
 * currentPage = 97, totalPages = 100, maxVisiblePages = 7
 * // returns [1, 'firstSpacer', 96, 97, 98, 99, 100]
 */
function getRange(currentPage, totalPages, maxVisiblePages) {
  // the total amount of visible pages is whatever's lower, totalPages or maxVisiblePages
  // maxVisiblePages has a lower limit of 5
  var visiblePages = Math.min(totalPages, Math.max(maxVisiblePages, 5));
  var min = 1;
  // the center part of the range starts with the current page minus half of the visible pages
  var centerStartPage = currentPage - Math.floor(visiblePages / 2);
  // centerStartPage has a lower limit of 1
  centerStartPage = Math.max(centerStartPage, min);
  // centerStartPage has an upper limit of 1 plus total pages minus visible pages
  centerStartPage = Math.min(centerStartPage, min + totalPages - visiblePages);
  var pages = Array.from({
    length: visiblePages
  }, function (_el, i) {
    return centerStartPage + i;
  }).reduce(function (acc, pageNr, index) {
    if (index === 0 && pageNr !== 1) {
      return [1, 'firstSpacer'];
    }
    if (totalPages > visiblePages && index === visiblePages - 2 && currentPage < totalPages - 2) {
      return [].concat(_toConsumableArray(acc), ['lastSpacer', totalPages]);
    }
    // Skip a number when spacer is already added
    if (acc.includes('firstSpacer') && index === 1 || acc.includes('lastSpacer') && index === visiblePages - 1) {
      return acc;
    }
    return [].concat(_toConsumableArray(acc), [pageNr]);
  }, []);
  return pages;
}
var Pagination = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$maxVisiblePages = _ref.maxVisiblePages,
    maxVisiblePages = _ref$maxVisiblePages === void 0 ? 7 : _ref$maxVisiblePages,
    _ref$nextLabel = _ref.nextLabel,
    nextLabel = _ref$nextLabel === void 0 ? 'volgende' : _ref$nextLabel,
    _ref$nextVisuallyHidd = _ref.nextVisuallyHiddenLabel,
    nextVisuallyHiddenLabel = _ref$nextVisuallyHidd === void 0 ? 'Volgende pagina' : _ref$nextVisuallyHidd,
    onPageChange = _ref.onPageChange,
    _ref$page = _ref.page,
    page = _ref$page === void 0 ? 1 : _ref$page,
    _ref$previousLabel = _ref.previousLabel,
    previousLabel = _ref$previousLabel === void 0 ? 'vorige' : _ref$previousLabel,
    _ref$previousVisually = _ref.previousVisuallyHiddenLabel,
    previousVisuallyHiddenLabel = _ref$previousVisually === void 0 ? 'Vorige pagina' : _ref$previousVisually,
    totalPages = _ref.totalPages,
    _ref$visuallyHiddenLa = _ref.visuallyHiddenLabel,
    visuallyHiddenLabel = _ref$visuallyHiddenLa === void 0 ? 'Paginering' : _ref$visuallyHiddenLa,
    restProps = _objectWithoutProperties(_ref, _excluded$t);
  var _useState = useState(page),
    _useState2 = _slicedToArray(_useState, 2),
    currentPage = _useState2[0],
    setCurrentPage = _useState2[1];
  // Get array of page numbers and / or spacers
  var range = useMemo(function () {
    return getRange(currentPage, totalPages, maxVisiblePages);
  }, [currentPage, totalPages, maxVisiblePages]);
  var onChangePage = function onChangePage(newPage) {
    if (onPageChange !== undefined) {
      onPageChange(newPage);
    }
    setCurrentPage(newPage);
  };
  var onPrevious = function onPrevious() {
    onChangePage(currentPage - 1);
  };
  var onNext = function onNext() {
    onChangePage(currentPage + 1);
  };
  var navLabelId = useId();
  // Don't show pagination if you only have one page
  if (totalPages <= 1) {
    return null;
  }
  return jsxs("nav", _objectSpread$t(_objectSpread$t({}, restProps), {}, {
    "aria-labelledby": navLabelId,
    className: clsx('ams-pagination', className),
    ref: ref,
    children: [jsx("span", {
      id: navLabelId,
      className: "ams-visually-hidden",
      children: visuallyHiddenLabel
    }), jsxs("ol", {
      className: "ams-pagination__list",
      children: [jsx("li", {
        children: jsxs("button", {
          className: "ams-pagination__button",
          disabled: currentPage === 1,
          onClick: onPrevious,
          type: "button",
          children: [jsx(Icon, {
            svg: ChevronLeftIcon,
            size: "level-5"
          }), jsx("span", {
            className: "ams-visually-hidden",
            children: previousVisuallyHiddenLabel
          }), jsx("span", {
            "aria-hidden": true,
            children: previousLabel
          })]
        })
      }), range.map(function (pageNumberOrSpacer) {
        return typeof pageNumberOrSpacer === 'number' ? jsx("li", {
          children: jsxs("button", {
            "aria-current": pageNumberOrSpacer === currentPage ? true : undefined,
            className: clsx('ams-pagination__button', pageNumberOrSpacer === currentPage && 'ams-pagination__button--current'),
            onClick: function onClick() {
              return pageNumberOrSpacer !== currentPage && onChangePage(pageNumberOrSpacer);
            },
            tabIndex: pageNumberOrSpacer === currentPage ? -1 : 0,
            type: "button",
            children: [jsx("span", {
              className: "ams-visually-hidden",
              children: pageNumberOrSpacer === currentPage ? "Pagina ".concat(pageNumberOrSpacer) : "Ga naar pagina ".concat(pageNumberOrSpacer)
            }), jsx("span", {
              "aria-hidden": true,
              children: pageNumberOrSpacer
            })]
          })
        }, pageNumberOrSpacer) : jsx("li", {
          "aria-hidden": true,
          "data-testid": pageNumberOrSpacer,
          children: "\u2026"
        }, pageNumberOrSpacer);
      }), jsx("li", {
        children: jsxs("button", {
          className: "ams-pagination__button",
          disabled: currentPage === totalPages,
          onClick: onNext,
          type: "button",
          children: [jsx("span", {
            className: "ams-visually-hidden",
            children: nextVisuallyHiddenLabel
          }), jsx("span", {
            "aria-hidden": true,
            children: nextLabel
          }), jsx(Icon, {
            svg: ChevronRightIcon,
            size: "level-5"
          })]
        })
      })]
    })]
  }));
});
Pagination.displayName = 'Pagination';

var _excluded$s = ["children", "className", "fullHeight", "maxWidth"];
function ownKeys$s(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$s(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$s(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$s(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Screen = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    fullHeight = _ref.fullHeight,
    _ref$maxWidth = _ref.maxWidth,
    maxWidth = _ref$maxWidth === void 0 ? 'wide' : _ref$maxWidth,
    restProps = _objectWithoutProperties(_ref, _excluded$s);
  return jsx("div", _objectSpread$s(_objectSpread$s({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-screen', fullHeight && 'ams-screen--full-height', "ams-screen--".concat(maxWidth), className),
    children: children
  }));
});
Screen.displayName = 'Screen';

var _excluded$r = ["className", "id"];
function ownKeys$r(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$r(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$r(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$r(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Switch = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    id = _ref.id,
    restProps = _objectWithoutProperties(_ref, _excluded$r);
  var inputId = id || useId();
  return jsxs("div", {
    className: clsx('ams-switch', className),
    children: [jsx("input", _objectSpread$r(_objectSpread$r({}, restProps), {}, {
      className: "ams-switch__input",
      id: inputId,
      ref: ref,
      type: "checkbox",
      role: "switch"
    })), jsx("label", {
      className: "ams-switch__label",
      htmlFor: inputId
    })]
  });
});
Switch.displayName = 'Switch';

var _excluded$q = ["children", "className", "as", "color"];
function ownKeys$q(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$q(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$q(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$q(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Spotlight = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$as = _ref.as,
    Tag = _ref$as === void 0 ? 'div' : _ref$as,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'dark-blue' : _ref$color,
    restProps = _objectWithoutProperties(_ref, _excluded$q);
  return jsx(Tag, _objectSpread$q(_objectSpread$q({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-spotlight', "ams-spotlight--".concat(color), className),
    children: children
  }));
});
Spotlight.displayName = 'Spotlight';

var _excluded$p = ["children", "className", "inverseColor", "size"];
function ownKeys$p(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$p(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$p(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$p(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Paragraph = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    size = _ref.size,
    restProps = _objectWithoutProperties(_ref, _excluded$p);
  return jsx("p", _objectSpread$p(_objectSpread$p({
    ref: ref,
    className: clsx('ams-paragraph', size && "ams-paragraph--".concat(size), inverseColor && 'ams-paragraph--inverse-color', className)
  }, restProps), {}, {
    children: children
  }));
});
Paragraph.displayName = 'Paragraph';

var _excluded$o = ["children", "className", "tagline"];
function ownKeys$o(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$o(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$o(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$o(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var CardHeadingGroup = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    tagline = _ref.tagline,
    restProps = _objectWithoutProperties(_ref, _excluded$o);
  return jsxs("hgroup", _objectSpread$o(_objectSpread$o({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-card__heading-group', className),
    children: [children, jsx(Paragraph, {
      size: "small",
      children: tagline
    })]
  }));
});
CardHeadingGroup.displayName = 'Card.HeadingGroup';

var _excluded$n = ["children", "className"];
function ownKeys$n(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$n(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$n(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$n(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var CardLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$n);
  return jsx("a", _objectSpread$n(_objectSpread$n({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-card__link', className),
    children: children
  }));
});
CardLink.displayName = 'Card.Link';

var _excluded$m = ["children", "className"];
function ownKeys$m(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$m(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$m(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$m(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var CardRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$m);
  return jsx("article", _objectSpread$m(_objectSpread$m({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-card', className),
    children: children
  }));
});
CardRoot.displayName = 'Card';
var Card = Object.assign(CardRoot, {
  HeadingGroup: CardHeadingGroup,
  Link: CardLink
});

var _excluded$l = ["children", "className", "ratio"];
function ownKeys$l(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$l(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$l(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$l(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var AspectRatio = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$ratio = _ref.ratio,
    ratio = _ref$ratio === void 0 ? 'square' : _ref$ratio,
    restProps = _objectWithoutProperties(_ref, _excluded$l);
  return jsx("div", _objectSpread$l(_objectSpread$l({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-aspect-ratio', "ams-aspect-ratio--".concat(ratio), className),
    children: children
  }));
});
AspectRatio.displayName = 'AspectRatio';

var _excluded$k = ["children", "className"];
function ownKeys$k(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$k(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$k(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$k(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FooterBottom = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$k);
  return jsx("div", _objectSpread$k(_objectSpread$k({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-footer__bottom', className),
    children: children
  }));
});
FooterBottom.displayName = 'Footer.Bottom';

var _excluded$j = ["children", "className"];
function ownKeys$j(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$j(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$j(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$j(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FooterTop = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$j);
  return jsx(Spotlight, _objectSpread$j(_objectSpread$j({}, restProps), {}, {
    color: "dark-blue",
    ref: ref,
    className: clsx('ams-footer__top', className),
    children: children
  }));
});
FooterTop.displayName = 'Footer.Top';

var _excluded$i = ["children", "className"];
function ownKeys$i(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$i(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$i(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$i(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var FooterRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$i);
  return jsx("footer", _objectSpread$i(_objectSpread$i({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-footer', className),
    children: children
  }));
});
FooterRoot.displayName = 'Footer';
var Footer = Object.assign(FooterRoot, {
  Bottom: FooterBottom,
  Top: FooterTop
});

var _excluded$h = ["children", "className", "icon"];
function ownKeys$h(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$h(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$h(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$h(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PageMenuLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    icon = _ref.icon,
    restProps = _objectWithoutProperties(_ref, _excluded$h);
  return jsx("li", {
    children: jsxs("a", _objectSpread$h(_objectSpread$h({}, restProps), {}, {
      ref: ref,
      className: clsx('ams-page-menu__link', className),
      children: [children, icon && jsx(Icon, {
        svg: icon,
        size: "level-6"
      })]
    }))
  });
});
PageMenuLink.displayName = 'PageMenu.Link';

var _excluded$g = ["alignEnd", "children", "className", "wrap"];
function ownKeys$g(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$g(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$g(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$g(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PageMenuRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var alignEnd = _ref.alignEnd,
    children = _ref.children,
    className = _ref.className,
    _ref$wrap = _ref.wrap,
    wrap = _ref$wrap === void 0 ? true : _ref$wrap,
    restProps = _objectWithoutProperties(_ref, _excluded$g);
  return jsx("ul", _objectSpread$g(_objectSpread$g({}, restProps), {}, {
    className: clsx('ams-page-menu', alignEnd && "ams-page-menu--align-end", !wrap && "ams-page-menu--no-wrap", className),
    ref: ref,
    children: children
  }));
});
PageMenuRoot.displayName = 'PageMenu';
var PageMenu = Object.assign(PageMenuRoot, {
  Link: PageMenuLink
});

var _excluded$f = ["className", "label", "description"];
function ownKeys$f(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$f(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$f(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$f(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var TopTaskLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    label = _ref.label,
    description = _ref.description,
    restProps = _objectWithoutProperties(_ref, _excluded$f);
  return jsxs("a", _objectSpread$f(_objectSpread$f({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-top-task-link', className),
    children: [jsx("span", {
      className: "ams-top-task-link__label",
      children: label
    }), jsx("span", {
      className: "ams-visually-hidden",
      children: ","
    }), jsx("span", {
      className: "ams-top-task-link__description",
      children: description
    })]
  }));
});
TopTaskLink.displayName = 'TopTaskLink';

var _excluded$e = ["children", "className", "inverseColor"];
function ownKeys$e(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$e(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$e(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$e(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Blockquote = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    restProps = _objectWithoutProperties(_ref, _excluded$e);
  return jsx("blockquote", _objectSpread$e(_objectSpread$e({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-blockquote', inverseColor && 'ams-blockquote--inverse-color', className),
    children: children
  }));
});
Blockquote.displayName = 'Blockquote';

var _excluded$d = ["children", "className", "invalid", "indeterminate"];
function ownKeys$d(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$d(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$d(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$d(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Checkbox = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    invalid = _ref.invalid,
    indeterminate = _ref.indeterminate,
    restProps = _objectWithoutProperties(_ref, _excluded$d);
  var id = useId();
  var innerRef = useRef(null);
  // use a passed ref if it's there, otherwise use innerRef
  useImperativeHandle(ref, function () {
    return innerRef.current;
  });
  // set input to indeterminate
  useEffect(function () {
    if (innerRef.current) {
      innerRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [innerRef, indeterminate]);
  return (
    // This div is here because NVDA doesn't match the input to the label
    // without a containing element
    jsxs("div", {
      className: clsx('ams-checkbox', className),
      children: [jsx("input", _objectSpread$d(_objectSpread$d({}, restProps), {}, {
        "aria-invalid": invalid || undefined,
        className: "ams-checkbox__input",
        id: id,
        ref: innerRef,
        type: "checkbox"
      })), jsxs("label", {
        className: "ams-checkbox__label",
        htmlFor: id,
        children: [jsx("span", {
          className: "ams-checkbox__checkmark"
        }), children]
      })]
    })
  );
});
Checkbox.displayName = 'Checkbox';

var _excluded$c = ["children", "className", "inverseColor"];
function ownKeys$c(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$c(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$c(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$c(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PageHeading = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    restProps = _objectWithoutProperties(_ref, _excluded$c);
  return jsx("h1", _objectSpread$c(_objectSpread$c({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-page-heading', inverseColor && 'ams-page-heading--inverse-color', className),
    children: children
  }));
});
PageHeading.displayName = 'PageHeading';

var _excluded$b = ["children", "className"];
function ownKeys$b(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$b(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$b(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$b(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var OrderedListItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$b);
  return jsx("li", _objectSpread$b(_objectSpread$b({
    ref: ref,
    className: clsx('ams-ordered-list__item', className)
  }, restProps), {}, {
    children: children
  }));
});
OrderedListItem.displayName = 'OrderedList.Item';

var _excluded$a = ["children", "className", "inverseColor", "markers", "size"];
function ownKeys$a(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$a(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$a(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$a(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var OrderedListRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    _ref$markers = _ref.markers,
    markers = _ref$markers === void 0 ? true : _ref$markers,
    size = _ref.size,
    restProps = _objectWithoutProperties(_ref, _excluded$a);
  return jsx("ol", _objectSpread$a(_objectSpread$a({
    ref: ref,
    className: clsx('ams-ordered-list', inverseColor && 'ams-ordered-list--inverse-color', !markers && 'ams-ordered-list--no-markers', size && "ams-ordered-list--".concat(size), className)
  }, restProps), {}, {
    children: children
  }));
});
OrderedListRoot.displayName = 'OrderedList';
var OrderedList = Object.assign(OrderedListRoot, {
  Item: OrderedListItem
});

var _excluded$9 = ["children", "className"];
function ownKeys$9(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$9(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$9(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$9(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var BreadcrumbLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$9);
  return jsx("li", {
    className: "ams-breadcrumb__item",
    children: jsx("a", _objectSpread$9(_objectSpread$9({}, restProps), {}, {
      className: clsx$1('ams-breadcrumb__link', className),
      ref: ref,
      children: children
    }))
  });
});
BreadcrumbLink.displayName = 'Breadcrumb.Link';

var _excluded$8 = ["children", "className"];
function ownKeys$8(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$8(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$8(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$8(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var BreadcrumbRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$8);
  return jsx("nav", _objectSpread$8(_objectSpread$8({}, restProps), {}, {
    className: clsx$1('ams-breadcrumb', className),
    ref: ref,
    children: jsx("ol", {
      className: "ams-breadcrumb__list",
      children: children
    })
  }));
});
BreadcrumbRoot.displayName = 'Breadcrumb';
var Breadcrumb = Object.assign(BreadcrumbRoot, {
  Link: BreadcrumbLink
});

var _excluded$7 = ["children", "className", "contrastColor", "inverseColor", "variant"];
function ownKeys$7(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$7(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$7(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$7(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Link = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    contrastColor = _ref.contrastColor,
    inverseColor = _ref.inverseColor,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'standalone' : _ref$variant,
    restProps = _objectWithoutProperties(_ref, _excluded$7);
  return jsx("a", _objectSpread$7(_objectSpread$7({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-link', contrastColor && 'ams-link--contrast-color', inverseColor && 'ams-link--inverse-color', variant === 'inline' && 'ams-link--inline', variant === 'standalone' && 'ams-link--standalone', className),
    children: children
  }));
});
Link.displayName = 'Link';

var _excluded$6 = ["children", "className", "disabled", "icon", "iconBefore", "iconOnly", "type", "variant"];
function ownKeys$6(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$6(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$6(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$6(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    disabled = _ref.disabled,
    icon = _ref.icon,
    iconBefore = _ref.iconBefore,
    iconOnly = _ref.iconOnly,
    type = _ref.type,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'primary' : _ref$variant,
    restProps = _objectWithoutProperties(_ref, _excluded$6);
  return jsxs("button", _objectSpread$6(_objectSpread$6({}, restProps), {}, {
    className: clsx('ams-button', "ams-button--".concat(variant), icon && iconOnly && "ams-button--icon-only", className),
    disabled: disabled,
    ref: ref,
    type: type || 'button',
    children: [icon && (iconBefore || iconOnly) && jsx(Icon, {
      svg: icon,
      size: "level-5",
      square: iconOnly
    }), icon && iconOnly ? jsx("span", {
      className: "ams-visually-hidden",
      children: children
    }) : children, icon && !iconBefore && !iconOnly && jsx(Icon, {
      svg: icon,
      size: "level-5"
    })]
  }));
});
Button.displayName = 'Button';

var _excluded$5 = ["children", "className", "hint", "optional"];
function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Label = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    hint = _ref.hint,
    optional = _ref.optional,
    restProps = _objectWithoutProperties(_ref, _excluded$5);
  return jsxs("label", _objectSpread$5(_objectSpread$5({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-label', className),
    children: [children, " ", jsx(Hint, {
      hint: hint,
      optional: optional
    })]
  }));
});
Label.displayName = 'Label';

var _excluded$4 = ["children", "className"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var UnorderedListItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$4);
  return jsx("li", _objectSpread$4(_objectSpread$4({
    ref: ref,
    className: clsx('ams-unordered-list__item', className)
  }, restProps), {}, {
    children: children
  }));
});
UnorderedListItem.displayName = 'UnorderedList.Item';

var _excluded$3 = ["children", "className", "inverseColor", "markers", "size"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var UnorderedListRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    inverseColor = _ref.inverseColor,
    _ref$markers = _ref.markers,
    markers = _ref$markers === void 0 ? true : _ref$markers,
    size = _ref.size,
    restProps = _objectWithoutProperties(_ref, _excluded$3);
  return jsx("ul", _objectSpread$3(_objectSpread$3({
    ref: ref,
    className: clsx('ams-unordered-list', inverseColor && 'ams-unordered-list--inverse-color', !markers && 'ams-unordered-list--no-markers', size && "ams-unordered-list--".concat(size), className)
  }, restProps), {}, {
    children: children
  }));
});
UnorderedListRoot.displayName = 'UnorderedList';
var UnorderedList = Object.assign(UnorderedListRoot, {
  Item: UnorderedListItem
});

/**
 * @license EUPL-1.2+
 * Copyright Gemeente Amsterdam
 */
var defaultValues = {
  headingLevel: 1,
  sectionAs: 'section'
};
var AccordionContext = /*#__PURE__*/createContext(defaultValues);

var _excluded$2 = ["label", "expanded", "children", "className"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var AccordionSection = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var label = _ref.label,
    _ref$expanded = _ref.expanded,
    expanded = _ref$expanded === void 0 ? false : _ref$expanded,
    children = _ref.children,
    className = _ref.className,
    restProps = _objectWithoutProperties(_ref, _excluded$2);
  var _useContext = useContext(AccordionContext),
    headingLevel = _useContext.headingLevel,
    sectionAs = _useContext.sectionAs;
  var _useState = useState(expanded),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var HeadingTag = getHeadingTag(headingLevel);
  var SectionTag = sectionAs || 'section';
  var id = useId();
  var buttonId = "button-".concat(id);
  var panelId = "panel-".concat(id);
  return jsxs("div", _objectSpread$2(_objectSpread$2({
    className: clsx('ams-accordion__section', className),
    ref: ref
  }, restProps), {}, {
    children: [jsx(HeadingTag, {
      className: "ams-accordion__header",
      children: jsxs("button", {
        "aria-controls": panelId,
        "aria-expanded": isExpanded,
        className: "ams-accordion__button",
        id: buttonId,
        onClick: function onClick() {
          return setIsExpanded(!isExpanded);
        },
        type: "button",
        children: [jsx(Icon, {
          svg: ChevronDownIcon,
          size: "level-5",
          className: "ams-accordion__icon"
        }), label]
      })
    }), jsx(SectionTag, {
      "aria-labelledby": buttonId,
      className: clsx('ams-accordion__panel', {
        'ams-accordion__panel--expanded': isExpanded
      }),
      id: panelId,
      children: children
    })]
  }));
});
AccordionSection.displayName = 'Accordion.Section';

var AccordionRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    headingLevel = _ref.headingLevel,
    _ref$sectionAs = _ref.sectionAs,
    sectionAs = _ref$sectionAs === void 0 ? 'section' : _ref$sectionAs;
  var innerRef = useRef(null);
  // use a passed ref if it's there, otherwise use innerRef
  useImperativeHandle(ref, function () {
    return innerRef.current;
  });
  var _useKeyboardFocus = useKeyboardFocus(innerRef, {
      rotating: true
    }),
    keyDown = _useKeyboardFocus.keyDown;
  return jsx(AccordionContext.Provider, {
    value: {
      headingLevel: headingLevel,
      sectionAs: sectionAs
    },
    children: jsx("div", {
      className: clsx('ams-accordion', className),
      onKeyDown: keyDown,
      ref: innerRef,
      children: children
    })
  });
});
AccordionRoot.displayName = 'Accordion';
var Accordion = Object.assign(AccordionRoot, {
  Section: AccordionSection
});

var _excluded$1 = ["as", "children", "className", "span", "start"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var GridCell = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$as = _ref.as,
    Tag = _ref$as === void 0 ? 'div' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    span = _ref.span,
    start = _ref.start,
    restProps = _objectWithoutProperties(_ref, _excluded$1);
  return jsx(Tag, _objectSpread$1(_objectSpread$1({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-grid__cell', gridCellClasses(span, start), className),
    children: children
  }));
});
GridCell.displayName = 'Grid.Cell';

var _excluded = ["children", "className", "gapVertical", "paddingBottom", "paddingTop", "paddingVertical"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var GridRoot = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    gapVertical = _ref.gapVertical,
    paddingBottom = _ref.paddingBottom,
    paddingTop = _ref.paddingTop,
    paddingVertical = _ref.paddingVertical,
    restProps = _objectWithoutProperties(_ref, _excluded);
  return jsx("div", _objectSpread(_objectSpread({}, restProps), {}, {
    ref: ref,
    className: clsx('ams-grid', gapVertical && "ams-grid--gap-vertical--".concat(gapVertical), paddingClasses('grid', paddingBottom, paddingTop, paddingVertical), className),
    children: children
  }));
});
GridRoot.displayName = 'Grid';
var Grid = Object.assign(GridRoot, {
  Cell: GridCell
});

export { Accordion, ActionGroup, Alert, AspectRatio, Avatar, Badge, Blockquote, Breadcrumb, Breakout, Button, Card, CharacterCount, Checkbox, Column, DateInput, DescriptionList, Dialog, ErrorMessage, Field, FieldSet, FileInput, Footer, FormErrorList, Grid, Header, Heading, Hint, Icon, IconButton, Image, ImageSlider, Label, Link, LinkList, Logo, Mark, MegaMenu, OrderedList, Overlap, PageHeading, PageMenu, Pagination, Paragraph, PasswordInput, Radio, Row, Screen, SearchField, Select, SkipLink, Spotlight, Switch, Table, TableOfContents, Tabs, TextArea, TextInput, TimeInput, TopTaskLink, UnorderedList };
//# sourceMappingURL=index.esm.js.map
